import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ReactDOM from 'react-dom';
import {
  Utensils,
  Plus,
  Search,
  Edit,
  Trash2,
  Upload,
  Download,
  FileText,
  AlertCircle,
  CheckCircle,
  X,
  Image as ImageIcon,
  Filter,
  Package,
  Settings,
  Eye,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const MenuManagement = () => {
  const { user, token } = useAuth();
  const [activeMenuCategory, setActiveMenuCategory] = useState(null);
  const [menuCategories, setMenuCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [allMenuItems, setAllMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStockStatus, setSelectedStockStatus] = useState('all');
  const [productCategories, setProductCategories] = useState([]);
  const [showCSVSection, setShowCSVSection] = useState(false);
  const [showCategoryOverlay, setShowCategoryOverlay] = useState(false);
  
  // Manage Product Categories Modal state
  const [showProductCategoriesModal, setShowProductCategoriesModal] = useState(false);
  const [managedCategories, setManagedCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [deletingCategory, setDeletingCategory] = useState(null);
  
  // Image browser state
  const [showImageBrowser, setShowImageBrowser] = useState(false);
  const [s3Images, setS3Images] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;
  
  // Modals
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [showCSVImportModal, setShowCSVImportModal] = useState(false);
  const [showCSVFormatModal, setShowCSVFormatModal] = useState(false);
  const [showManageCategoriesModal, setShowManageCategoriesModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showDeleteProductModal, setShowDeleteProductModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [itemForm, setItemForm] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    in_stock: true,
    quantity: '',
    weight: '',
    size: '',
    image_url: '',
    s3_key: '',
    media_gallery: [], // Array of {id, type, url, s3_key, thumbnail_url, order}
    is_variable: false,
    variation_type: 'size',
    variants: [],
    has_options: false,
    options: [],
    // Bargain Mode fields
    bargain_enabled: false,
    min_bargain_price: '',
    // Component Display field
    component_display_enabled: false
  });
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: ''
  });
  
  const [csvFile, setCSVFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [csvImportResult, setCsvImportResult] = useState(null);
  
  // Confirmation modal state (for menu category deletion)
  const [deleteCategoryData, setDeleteCategoryData] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [showAIGenerateModal, setShowAIGenerateModal] = useState(false);
  const [aiGenerateInput, setAIGenerateInput] = useState({ name: '', description: '' });
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [aiGenerateError, setAIGenerateError] = useState('');
  
  // Variant type state (single type like Service Management)
  const [variantType, setVariantType] = useState('Size');
  
  // AI Image Generation states
  const [showAIImageModal, setShowAIImageModal] = useState(false);
  const [aiImageInput, setAIImageInput] = useState({ name: '', description: '' });
  const [generatingImage, setGeneratingImage] = useState(false);
  const [aiImageError, setAIImageError] = useState('');
  const [availableImageModels, setAvailableImageModels] = useState([]);
  const [selectedImageModel, setSelectedImageModel] = useState('');
  
  // Reference image states for AI generation
  const [useReferenceImage, setUseReferenceImage] = useState(false);
  const [referenceImageUrl, setReferenceImageUrl] = useState('');
  const [referenceImageFile, setReferenceImageFile] = useState(null);
  const [showReferenceImageBrowser, setShowReferenceImageBrowser] = useState(false);
  const [referenceS3Images, setReferenceS3Images] = useState([]);
  const [referenceSearchQuery, setReferenceSearchQuery] = useState('');
  const [loadingReferenceImages, setLoadingReferenceImages] = useState(false);
  const [referenceImageMode, setReferenceImageMode] = useState('none'); // 'none', 'reference', 'product_cover'
  const [aiImageTargetGallery, setAiImageTargetGallery] = useState(false); // If true, add to gallery instead of replacing main image
  
  // AI Generate Image Slider states
  const [showAISliderModal, setShowAISliderModal] = useState(false);
  const [sliderPrompt, setSliderPrompt] = useState('');
  const [sliderImageCount, setSliderImageCount] = useState(4);
  const [sliderReferenceImages, setSliderReferenceImages] = useState([]); // Array of {id, url, file, type: 'upload'|'s3'} - PRODUCT images
  const [showSliderReferenceBrowser, setShowSliderReferenceBrowser] = useState(false);
  const [sliderReferenceS3Images, setSliderReferenceS3Images] = useState([]);
  const [sliderReferenceSearchQuery, setSliderReferenceSearchQuery] = useState('');
  const [loadingSliderReferenceImages, setLoadingSliderReferenceImages] = useState(false);
  const [generatingSliderImages, setGeneratingSliderImages] = useState(false);
  const [sliderGenerationProgress, setSliderGenerationProgress] = useState(0);
  const [generatedSliderImages, setGeneratedSliderImages] = useState([]);
  const [sliderError, setSliderError] = useState('');
  
  // Face Reference Image state (separate from product images)
  const [faceReferenceImage, setFaceReferenceImage] = useState(null); // {id, url, file, type: 'upload'|'s3'}
  const [showFaceReferenceBrowser, setShowFaceReferenceBrowser] = useState(false);
  const [faceReferenceS3Images, setFaceReferenceS3Images] = useState([]);
  const [faceReferenceSearchQuery, setFaceReferenceSearchQuery] = useState('');
  const [loadingFaceReferenceImages, setLoadingFaceReferenceImages] = useState(false);
  
  // S3 Image Search states
  const [imageSearchQuery, setImageSearchQuery] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  
  // Image preview modal state
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [previewImages, setPreviewImages] = useState([]); // Array of all images for slider
  const [previewImageIndex, setPreviewImageIndex] = useState(0); // Current image index
  
  // Media Gallery states
  const [showMediaGalleryBrowser, setShowMediaGalleryBrowser] = useState(false);
  const [mediaGallerySearchQuery, setMediaGallerySearchQuery] = useState('');
  const [mediaGalleryS3Images, setMediaGalleryS3Images] = useState([]);
  const [loadingMediaGalleryImages, setLoadingMediaGalleryImages] = useState(false);
  const [videoUrlInput, setVideoUrlInput] = useState('');
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (user && token) {
      fetchMenuCategories();
    }
  }, [user, token]);


  // Fetch available image models on component mount
  useEffect(() => {
    const fetchImageModels = async () => {
      try {
        // Check if using OpenAI for all services
        const openaiResponse = await fetch(`${API_BASE_URL}/api/openai-settings`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (openaiResponse.ok) {
          const openaiData = await openaiResponse.json();
          if (openaiData.success && openaiData.settings && openaiData.settings.use_openai_for_all_services) {
            // Use all available image models (OpenAI DALL-E and Google Nano Banana)
            const imageModelsData = (openaiData.image_models || []).map(model => ({
              id: model.value || model.id,
              name: model.label || model.name
            }));
            
            setAvailableImageModels(imageModelsData);

            // Set default model from settings
            const defaultImageModel = openaiData.settings.image_generation_model || 'dall-e-3';
            setSelectedImageModel(defaultImageModel);

            console.log('🖼️ Loaded image models:', imageModelsData);
            console.log('🎯 Default image model:', defaultImageModel);
          }
        }
      } catch (error) {
        console.error('Error fetching image models:', error);
        // Set default fallback
        setAvailableImageModels([
          { id: 'dall-e-3', name: 'DALL-E 3' }
        ]);
        setSelectedImageModel('dall-e-3');
      }
    };

    if (user && token) {
      fetchImageModels();
    }
  }, [user, token, API_BASE_URL]);



  // Fetch managed categories when active menu category changes
  useEffect(() => {
    if (activeMenuCategory) {
      fetchManagedCategories();
    }
  }, [activeMenuCategory]);


  // Load filters from localStorage on mount
  useEffect(() => {
    if (activeMenuCategory) {
      const storageKey = `product_filters_${activeMenuCategory}`;
      const savedFilters = localStorage.getItem(storageKey);
      if (savedFilters) {
        try {
          const filters = JSON.parse(savedFilters);
          if (filters.category) setSelectedCategory(filters.category);
          if (filters.stockStatus) setSelectedStockStatus(filters.stockStatus);
        } catch (e) {
          console.error('Error loading filters from localStorage:', e);
        }
      }
    }
  }, [activeMenuCategory]);

  // Save filters to localStorage when they change
  useEffect(() => {
    if (activeMenuCategory) {
      const storageKey = `product_filters_${activeMenuCategory}`;
      const filters = {
        category: selectedCategory,
        stockStatus: selectedStockStatus
      };
      localStorage.setItem(storageKey, JSON.stringify(filters));
    }
  }, [selectedCategory, selectedStockStatus, activeMenuCategory]);


  useEffect(() => {
    if (activeMenuCategory) {
      fetchMenuItems();
      fetchAllMenuItems(); // Also fetch all items for category counts
    }
  }, [activeMenuCategory]);

  // Filter items when menuItems, searchTerm, selectedCategory, or selectedStockStatus changes
  useEffect(() => {
    filterItems();
  }, [menuItems, searchTerm, selectedCategory, selectedStockStatus]);

  // Reset to page 1 only when search or filter changes (not when menuItems changes)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedStockStatus]);

  const fetchMenuCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/menu-categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMenuCategories(data);
        if (data.length > 0 && !activeMenuCategory) {
          setActiveMenuCategory(data[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching menu categories:', error);
      showMessage('error', 'Failed to load menu categories');
    }
  };

  const fetchAllMenuItems = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/menu-items`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAllMenuItems(data);
      }
    } catch (error) {
      console.error('Error fetching all menu items:', error);
    }
  };

  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/menu-items?menu_category_id=${activeMenuCategory}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('📦 Fetched menu items:', data.length, 'items');
        // Log first few items with image URLs
        data.slice(0, 3).forEach(item => {
          console.log(`📦 Item: ${item.name}, image_url: ${item.image_url}`);
        });
        setMenuItems(data);
        
        // Extract unique product categories
        const categories = [...new Set(data.map(item => item.category))];
        setProductCategories(categories);
      }
    } catch (error) {
      console.error('Error fetching menu items:', error);
      showMessage('error', 'Failed to load menu items');
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = [...menuItems];
    
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (selectedCategory) {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }
    
    if (selectedStockStatus !== 'all') {
      if (selectedStockStatus === 'in_stock') {
        filtered = filtered.filter(item => item.in_stock === true);
      } else if (selectedStockStatus === 'out_of_stock') {
        filtered = filtered.filter(item => item.in_stock === false);
      }
    }
    
    setFilteredItems(filtered);
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleAddCategory = async () => {
    if (!categoryForm.name) {
      showMessage('error', 'Category name is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/menu-categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(categoryForm)
      });
      
      if (response.ok) {
        showMessage('success', 'Menu category created successfully');
        fetchMenuCategories();
        setCategoryForm({ name: '', description: '' });
        // Don't close modal - keep it open for adding more categories
      } else {
        showMessage('error', 'Failed to create menu category');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      showMessage('error', 'Failed to create menu category');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    // Show confirmation modal instead of browser confirm
    setDeleteCategoryData({ id: categoryId, name: categoryName });
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteCategory = async () => {
    if (!deleteCategoryData) return;
    
    const { id, name } = deleteCategoryData;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/menu-categories/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        showMessage('success', `Category "${name}" deleted successfully`);
        // If deleted category was active, switch to first category
        if (activeMenuCategory === id) {
          const remaining = menuCategories.filter(c => c.id !== id);
          if (remaining.length > 0) {
            setActiveMenuCategory(remaining[0].id);
          } else {
            setActiveMenuCategory(null);
          }
        }
        fetchMenuCategories();
        setShowDeleteConfirmModal(false);
        setDeleteCategoryData(null);
        setDeleteError(null);
      } else {
        const data = await response.json();
        // Show error in modal instead of hiding it
        setDeleteError(data.detail || 'Failed to delete menu category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      setDeleteError('Network error. Please try again.');
    }
  };

  const cancelDeleteCategory = () => {
    setShowDeleteConfirmModal(false);
    setDeleteCategoryData(null);
    setDeleteError(null);
  };

  const fetchS3Images = async (searchQuery = '') => {
    if (!searchQuery || !searchQuery.trim()) {
      // Don't search without a query
      setS3Images([]);
      return;
    }
    
    setLoadingImages(true);
    try {
      const queryParam = searchQuery.trim();
      console.log('🔍 Searching S3 images with query:', queryParam);
      const response = await fetch(`${API_BASE_URL}/api/menu-items/browse-images?search=${encodeURIComponent(queryParam)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ S3 Images data received:', data);
        console.log('📸 Number of images:', data.images?.length || 0);
        console.log('📁 Folder path:', data.folder);
        setS3Images(data.images || []);
        if (!data.images || data.images.length === 0) {
          showMessage('info', `No images found matching "${queryParam}". Try AI Generate or Upload to add new images.`);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        showMessage('error', `Failed to search images: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Error searching S3 images:', error);
      showMessage('error', `Failed to search images: ${error.message}`);
    } finally {
      setLoadingImages(false);
    }
  };
  
  const handleSearchS3Images = () => {
    if (imageSearchQuery.trim().length < 3) {
      showMessage('warning', 'Please enter at least 3 characters to search');
      return;
    }
    setHasSearched(true);
    fetchS3Images(imageSearchQuery);
  };

  // Fetch managed product categories for the current menu category (Pet Foods/Medications)
  const fetchManagedCategories = async () => {
    try {
      // Fetch menu items for the active menu category
      const response = await fetch(
        `${API_BASE_URL}/api/menu-items?menu_category_id=${activeMenuCategory}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const items = await response.json();
        // Extract unique product categories from menu items
        const uniqueCategories = [...new Set(items.map(item => item.category).filter(cat => cat))];
        
        // Get additional categories from localStorage
        const storageKey = `product_categories_${activeMenuCategory}`;
        const storedCategories = JSON.parse(localStorage.getItem(storageKey) || '[]');
        
        // Merge both sources and remove duplicates
        const allCategories = [...new Set([...uniqueCategories, ...storedCategories])];
        setManagedCategories(allCategories);
      }
    } catch (error) {
      console.error('Error fetching product categories:', error);
    }
  };

  // Add a new product category
  const handleAddProductCategory = async () => {
    if (!newCategoryName.trim()) {
      showMessage('warning', 'Please enter a category name');
      return;
    }

    if (managedCategories.includes(newCategoryName.trim())) {
      showMessage('warning', 'Category already exists');
      return;
    }

    const newCategory = newCategoryName.trim();
    const updatedCategories = [...managedCategories, newCategory];
    
    // Update local state
    setManagedCategories(updatedCategories);
    
    // Persist to localStorage
    const storageKey = `product_categories_${activeMenuCategory}`;
    localStorage.setItem(storageKey, JSON.stringify(updatedCategories));
    
    setNewCategoryName('');
    showMessage('success', 'Product category added successfully');
  };

  // Delete a product category
  const handleDeleteProductCategory = async (categoryName) => {
    // Find all products using this category
    const productsUsingCategory = menuItems.filter(item => item.category === categoryName);
    
    try {
      // Update all products using this category to have null category
      if (productsUsingCategory.length > 0) {
        const updatePromises = productsUsingCategory.map(product => 
          fetch(`${API_BASE_URL}/api/menu-items/${product.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...product,
              category: '' // Set to empty string (null equivalent)
            })
          })
        );
        
        await Promise.all(updatePromises);
        
        // Refresh menu items to show updated data
        await fetchMenuItems();
        await fetchAllMenuItems(); // Refresh all items for counts
      }

      // Remove category from managed list
      const updatedCategories = managedCategories.filter(cat => cat !== categoryName);
      
      // Update local state
      setManagedCategories(updatedCategories);
      
      // Update localStorage
      const storageKey = `product_categories_${activeMenuCategory}`;
      localStorage.setItem(storageKey, JSON.stringify(updatedCategories));
      
      showMessage('success', `Product category deleted. ${productsUsingCategory.length} product(s) updated.`);
      setDeletingCategory(null);
    } catch (error) {
      console.error('Error deleting product category:', error);
      showMessage('error', 'Failed to delete category');
      setDeletingCategory(null);
    }
  };


  const selectS3Image = (imageUrl, s3Key) => {
    console.log('📸 Selecting S3 image:', imageUrl);
    console.log('🔑 S3 key:', s3Key);
    setItemForm(prevForm => ({
      ...prevForm,
      image_url: imageUrl,
      s3_key: s3Key
    }));
    setImageFile(null);
    setShowImageBrowser(false);
    showMessage('success', 'Image selected successfully');
  };

  const handleAddItem = async () => {
    // Validate based on product type
    if (!itemForm.name || !itemForm.category) {
      showMessage('error', 'Name and category are required');
      return;
    }
    
    // Validate variable product
    if (itemForm.is_variable) {
      if (!variantType || variantType.trim() === '') {
        showMessage('error', 'Please provide a variant type name (e.g., Size, Weight, Package)');
        return;
      }
      if (!itemForm.variants || itemForm.variants.length === 0) {
        showMessage('error', 'Please add at least one variant for variable products');
        return;
      }
      // Check if all variants have required fields
      const invalidVariants = itemForm.variants.filter(v => !v.name || !v.price);
      if (invalidVariants.length > 0) {
        showMessage('error', 'All variants must have a name and price');
        return;
      }
    } else {
      // Validate simple product
      if (!itemForm.price) {
        showMessage('error', 'Price is required for simple products');
        return;
      }
    }
    
    // Validate product options
    if (itemForm.has_options) {
      if (!itemForm.options || itemForm.options.length === 0) {
        showMessage('error', 'Please add at least one option or disable "Product Options"');
        return;
      }
      // Check if all options have required fields
      const invalidOptions = itemForm.options.filter(opt => !opt.option_name);
      if (invalidOptions.length > 0) {
        showMessage('error', 'All options must have a name');
        return;
      }
      // Check dropdown options have choices
      const dropdownOptions = itemForm.options.filter(opt => opt.option_type === 'dropdown');
      for (const opt of dropdownOptions) {
        if (!opt.choices || opt.choices.length === 0) {
          showMessage('error', `Dropdown option "${opt.option_name}" must have at least one choice`);
          return;
        }
        const invalidChoices = opt.choices.filter(ch => !ch.choice_name);
        if (invalidChoices.length > 0) {
          showMessage('error', `All choices in "${opt.option_name}" must have a name`);
          return;
        }
      }
    }
    
    try {
      const itemData = {
        ...itemForm,
        price: itemForm.price ? parseFloat(itemForm.price) : 0,
        quantity: itemForm.quantity ? parseInt(itemForm.quantity) : null,
        menu_category_id: activeMenuCategory,
        // Bargain mode fields
        bargain_enabled: itemForm.bargain_enabled || false,
        min_bargain_price: itemForm.min_bargain_price ? parseFloat(itemForm.min_bargain_price) : null,
        // Component display field
        component_display_enabled: itemForm.component_display_enabled || false,
        // Convert variant prices and stock to numbers
        variants: itemForm.is_variable && itemForm.variants ? itemForm.variants.map(v => ({
          variant_type: variantType,
          name: v.name,
          price: parseFloat(v.price) || 0,
          stock: v.stock && v.stock !== '' ? parseInt(v.stock) : undefined
        })) : [],
        // Process options with proper type conversion
        options: itemForm.has_options && itemForm.options ? itemForm.options.map(opt => ({
          option_id: opt.option_id,
          option_name: opt.option_name,
          option_type: opt.option_type,
          is_required: opt.is_required || false,
          price: opt.option_type === 'single' && opt.price ? parseFloat(opt.price) : undefined,
          choices: opt.option_type === 'dropdown' && opt.choices ? opt.choices.map(ch => ({
            choice_id: ch.choice_id,
            choice_name: ch.choice_name,
            price: ch.price ? parseFloat(ch.price) : undefined
          })) : undefined
        })) : []
      };
      
      console.log('📝 Adding product with data:', itemData);
      
      const response = await fetch(`${API_BASE_URL}/api/menu-items`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(itemData)
      });
      
      if (response.ok) {
        const newItem = await response.json();
        
        // Upload image if selected
        if (imageFile && newItem.id) {
          const imageUrl = await uploadItemImage(newItem.id);
          if (!imageUrl) {
            showMessage('error', 'Product added but image upload failed');
          }
        }
        
        showMessage('success', 'Menu item added successfully');
        
        // Wait a moment for database to fully update before refreshing
        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchMenuItems();
        await fetchAllMenuItems(); // Refresh all items for counts
        
        setShowAddItemModal(false);
        resetItemForm();
      } else {
        showMessage('error', 'Failed to add menu item');
      }
    } catch (error) {
      console.error('Error adding item:', error);
      showMessage('error', 'Failed to add menu item');
    }
  };

  const handleUpdateItem = async () => {
    // Validate based on product type
    if (!itemForm.name || !itemForm.category) {
      showMessage('error', 'Name and category are required');
      return;
    }
    
    // Validate variable product
    if (itemForm.is_variable) {
      if (!variantType || variantType.trim() === '') {
        showMessage('error', 'Please provide a variant type name (e.g., Size, Weight, Package)');
        return;
      }
      if (!itemForm.variants || itemForm.variants.length === 0) {
        showMessage('error', 'Please add at least one variant for variable products');
        return;
      }
      // Check if all variants have required fields
      const invalidVariants = itemForm.variants.filter(v => !v.name || !v.price);
      if (invalidVariants.length > 0) {
        showMessage('error', 'All variants must have a name and price');
        return;
      }
    } else {
      // Validate simple product
      if (!itemForm.price) {
        showMessage('error', 'Price is required for simple products');
        return;
      }
    }
    
    // Validate product options
    if (itemForm.has_options) {
      if (!itemForm.options || itemForm.options.length === 0) {
        showMessage('error', 'Please add at least one option or disable "Product Options"');
        return;
      }
      // Check if all options have required fields
      const invalidOptions = itemForm.options.filter(opt => !opt.option_name);
      if (invalidOptions.length > 0) {
        showMessage('error', 'All options must have a name');
        return;
      }
      // Check dropdown options have choices
      const dropdownOptions = itemForm.options.filter(opt => opt.option_type === 'dropdown');
      for (const opt of dropdownOptions) {
        if (!opt.choices || opt.choices.length === 0) {
          showMessage('error', `Dropdown option "${opt.option_name}" must have at least one choice`);
          return;
        }
        const invalidChoices = opt.choices.filter(ch => !ch.choice_name);
        if (invalidChoices.length > 0) {
          showMessage('error', `All choices in "${opt.option_name}" must have a name`);
          return;
        }
      }
    }
    
    try {
      const itemData = {
        ...itemForm,
        price: itemForm.price ? parseFloat(itemForm.price) : 0,
        quantity: itemForm.quantity ? parseInt(itemForm.quantity) : null,
        // Bargain mode fields
        bargain_enabled: itemForm.bargain_enabled || false,
        min_bargain_price: itemForm.min_bargain_price ? parseFloat(itemForm.min_bargain_price) : null,
        // Component display field
        component_display_enabled: itemForm.component_display_enabled || false,
        // Convert variant prices and stock to numbers
        variants: itemForm.is_variable && itemForm.variants ? itemForm.variants.map(v => ({
          variant_type: variantType,
          name: v.name,
          price: parseFloat(v.price) || 0,
          stock: v.stock && v.stock !== '' ? parseInt(v.stock) : undefined
        })) : [],
        // Process options with proper type conversion
        options: itemForm.has_options && itemForm.options ? itemForm.options.map(opt => ({
          option_id: opt.option_id,
          option_name: opt.option_name,
          option_type: opt.option_type,
          is_required: opt.is_required || false,
          price: opt.option_type === 'single' && opt.price ? parseFloat(opt.price) : undefined,
          choices: opt.option_type === 'dropdown' && opt.choices ? opt.choices.map(ch => ({
            choice_id: ch.choice_id,
            choice_name: ch.choice_name,
            price: ch.price ? parseFloat(ch.price) : undefined
          })) : undefined
        })) : []
      };
      
      console.log('📝 Updating product with data:', itemData);
      console.log('🖼️ Image URL being sent:', itemData.image_url);
      console.log('🔑 S3 Key being sent:', itemData.s3_key);
      
      const response = await fetch(`${API_BASE_URL}/api/menu-items/${editingItem.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(itemData)
      });
      
      const responseData = await response.json();
      console.log('📬 Update response:', response.status, responseData);
      console.log('🖼️ Returned image_url:', responseData.image_url);
      
      if (response.ok) {
        // Upload image if new one selected
        if (imageFile) {
          const imageUrl = await uploadItemImage(editingItem.id);
          if (!imageUrl) {
            showMessage('error', 'Product updated but image upload failed');
          }
        }
        
        showMessage('success', 'Menu item updated successfully');
        
        // Wait a moment for database to fully update before refreshing
        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchMenuItems();
        await fetchAllMenuItems(); // Refresh all items for counts
        
        setShowEditItemModal(false);
        resetItemForm();
      } else {
        showMessage('error', 'Failed to update menu item');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      showMessage('error', 'Failed to update menu item');
    }
  };

  const uploadItemImage = async (itemId) => {
    if (!imageFile) return null;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      
      const response = await fetch(`${API_BASE_URL}/api/menu-items/${itemId}/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.image_url;
      } else {
        const errorData = await response.json();
        console.error('Image upload failed:', errorData);
        return null;
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    // Open confirmation modal instead of window.confirm
    setItemToDelete(itemId);
    setShowDeleteProductModal(true);
  };

  const toggleStockStatus = async (itemId, currentStatus) => {
    try {
      const item = menuItems.find(i => i.id === itemId);
      if (!item) return;

      const response = await fetch(`${API_BASE_URL}/api/menu-items/${itemId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...item,
          in_stock: !currentStatus
        })
      });

      if (response.ok) {
        showMessage('success', `Product marked as ${!currentStatus ? 'In Stock' : 'Out of Stock'}`);
        fetchMenuItems();
        fetchAllMenuItems(); // Refresh all items for counts
      } else {
        showMessage('error', 'Failed to update stock status');
      }
    } catch (error) {
      console.error('Error updating stock status:', error);
      showMessage('error', 'Failed to update stock status');
    }
  };

  const confirmDeleteItem = async () => {
    if (!itemToDelete) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/menu-items/${itemToDelete}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        showMessage('success', 'Product deleted successfully');
        fetchMenuItems();
        fetchAllMenuItems(); // Refresh all items for counts
      } else {
        showMessage('error', 'Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      showMessage('error', 'Failed to delete product');
    } finally {
      setShowDeleteProductModal(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteAll = async () => {
    // Open delete all confirmation modal
    setDeleteAllConfirm(true);
  };

  const confirmDeleteAll = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/menu-items?menu_category_id=${activeMenuCategory}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        showMessage('success', data.message);
        fetchMenuItems();
        fetchAllMenuItems(); // Refresh all items for counts
      } else {
        showMessage('error', 'Failed to delete products');
      }
    } catch (error) {
      console.error('Error deleting items:', error);
      showMessage('error', 'Failed to delete products');
    } finally {
      setDeleteAllConfirm(false);
    }
  };

  const handleCSVImport = async () => {
    if (!csvFile) {
      showMessage('error', 'Please select a CSV file');
      return;
    }
    
    setLoading(true);
    setCsvImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const response = await fetch(
        `${API_BASE_URL}/api/menu-items/import-csv/${activeMenuCategory}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setCsvImportResult(data);
        if (data.success) {
          fetchMenuItems();
          fetchAllMenuItems(); // Refresh all items for counts
        }
      } else {
        showMessage('error', 'Failed to import CSV');
      }
    } catch (error) {
      console.error('Error importing CSV:', error);
      showMessage('error', 'Failed to import CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleCSVExport = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/menu-items/export-csv/${activeMenuCategory}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `menu_items_${activeMenuCategory}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showMessage('success', 'CSV exported successfully');
      } else {
        showMessage('error', 'Failed to export CSV');
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      showMessage('error', 'Failed to export CSV');
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setItemForm({
      name: item.name,
      description: item.description || '',
      price: item.price.toString(),
      category: item.category,
      in_stock: item.in_stock,
      quantity: item.quantity?.toString() || '',
      weight: item.weight || '',
      size: item.size || '',
      image_url: item.image_url || '',
      s3_key: item.s3_key || '',
      media_gallery: item.media_gallery || [],
      is_variable: item.is_variable || false,
      variation_type: item.variation_type || 'size',
      variants: item.variants || [],
      has_options: item.has_options || false,
      options: item.options || [],
      bargain_enabled: item.bargain_enabled || false,
      min_bargain_price: item.min_bargain_price?.toString() || '',
      component_display_enabled: item.component_display_enabled || false
    });
    
    // Set variant type from existing variants or default
    if (item.is_variable && item.variants && item.variants.length > 0 && item.variants[0].variant_type) {
      setVariantType(item.variants[0].variant_type);
    } else {
      setVariantType('Size');
    }
    
    setImageFile(null);
    setShowEditItemModal(true);
  };

  const resetItemForm = () => {
    setItemForm({
      name: '',
      description: '',
      price: '',
      category: '',
      in_stock: true,
      quantity: '',
      weight: '',
      size: '',
      image_url: '',
      s3_key: '',
      media_gallery: [],
      is_variable: false,
      variation_type: 'size',
      variants: [],
      has_options: false,
      options: [],
      bargain_enabled: false,
      min_bargain_price: '',
      component_display_enabled: false
    });
    setVariantType('Size'); // Reset variant type
    setImageFile(null);
    setEditingItem(null);
  };

  const handleAIGenerateDescription = async () => {
    if (!aiGenerateInput.name || !aiGenerateInput.description) {
      setAIGenerateError('Please provide both product name and rough description');
      return;
    }

    setGeneratingDescription(true);
    setAIGenerateError('');
    
    try {
      console.log('🤖 Generating AI description for:', aiGenerateInput.name);
      
      const response = await fetch(`${API_BASE_URL}/api/menu-items/generate-description`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_name: aiGenerateInput.name,
          rough_description: aiGenerateInput.description
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ AI description generated:', data.description);
        console.log('📝 Updating description field with:', data.description);
        
        // Update the description field using functional update to avoid stale closure
        setItemForm(prevForm => {
          console.log('🔄 Previous form:', prevForm);
          const newForm = { ...prevForm, description: data.description };
          console.log('🔄 New form:', newForm);
          return newForm;
        });
        
        setShowAIGenerateModal(false);
        setAIGenerateInput({ name: '', description: '' });
        setAIGenerateError('');
        showMessage('success', 'Description generated successfully!');
      } else {
        const errorData = await response.json();
        console.error('❌ Error from backend:', errorData);
        setAIGenerateError(errorData.detail || 'Failed to generate description. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error generating description:', error);
      setAIGenerateError('Network error. Please check your connection and try again.');
    } finally {
      setGeneratingDescription(false);
    }
  };

  // Fetch S3 images for reference image selection - using the same API as browse images
  const fetchReferenceS3Images = async (query = '') => {
    if (!query || !query.trim()) {
      // Don't search without a query
      setReferenceS3Images([]);
      return;
    }
    
    setLoadingReferenceImages(true);
    try {
      const queryParam = query.trim();
      console.log('🔍 Searching reference S3 images with query:', queryParam);
      const response = await fetch(
        `${API_BASE_URL}/api/menu-items/browse-images?search=${encodeURIComponent(queryParam)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Reference S3 Images data received:', data);
        setReferenceS3Images(data.images || []);
      }
    } catch (error) {
      console.error('Error fetching reference S3 images:', error);
    } finally {
      setLoadingReferenceImages(false);
    }
  };
  
  // Handle reference S3 image search
  const handleReferenceSearchS3Images = () => {
    if (referenceSearchQuery.trim().length < 3) {
      showMessage('warning', 'Please enter at least 3 characters to search');
      return;
    }
    fetchReferenceS3Images(referenceSearchQuery);
  };

  // Handle reference image file upload
  const handleReferenceImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReferenceImageFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setReferenceImageUrl(previewUrl);
    }
  };

  // Clear reference image
  const clearReferenceImage = () => {
    setReferenceImageUrl('');
    setReferenceImageFile(null);
  };

  // Media Gallery Functions
  const fetchMediaGalleryS3Images = async (query = '') => {
    if (!query || !query.trim() || query.trim().length < 3) {
      setMediaGalleryS3Images([]);
      return;
    }
    
    setLoadingMediaGalleryImages(true);
    try {
      const queryParam = query.trim();
      const response = await fetch(
        `${API_BASE_URL}/api/menu-items/browse-images?search=${encodeURIComponent(queryParam)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMediaGalleryS3Images(data.images || []);
      }
    } catch (error) {
      console.error('Error fetching media gallery images:', error);
    } finally {
      setLoadingMediaGalleryImages(false);
    }
  };

  const handleMediaGallerySearch = () => {
    if (mediaGallerySearchQuery.trim().length < 3) {
      showMessage('warning', 'Please enter at least 3 characters to search');
      return;
    }
    fetchMediaGalleryS3Images(mediaGallerySearchQuery);
  };

  const addMediaToGallery = (url, s3Key = null, type = 'image') => {
    const newMedia = {
      id: `media_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: type,
      url: url,
      s3_key: s3Key,
      thumbnail_url: type === 'video' ? null : url,
      order: itemForm.media_gallery.length
    };
    
    setItemForm(prev => ({
      ...prev,
      media_gallery: [...prev.media_gallery, newMedia]
    }));
    
    showMessage('success', `${type === 'video' ? 'Video' : 'Image'} added to gallery`);
  };

  const removeMediaFromGallery = (mediaId) => {
    setItemForm(prev => ({
      ...prev,
      media_gallery: prev.media_gallery.filter(m => m.id !== mediaId).map((m, idx) => ({...m, order: idx}))
    }));
  };

  const moveMediaInGallery = (mediaId, direction) => {
    const gallery = [...itemForm.media_gallery];
    const index = gallery.findIndex(m => m.id === mediaId);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= gallery.length) return;
    
    [gallery[index], gallery[newIndex]] = [gallery[newIndex], gallery[index]];
    gallery.forEach((m, idx) => m.order = idx);
    
    setItemForm(prev => ({
      ...prev,
      media_gallery: gallery
    }));
  };

  const addVideoUrl = () => {
    if (!videoUrlInput.trim()) {
      showMessage('warning', 'Please enter a video URL');
      return;
    }
    
    // Basic URL validation
    try {
      new URL(videoUrlInput);
    } catch {
      showMessage('error', 'Please enter a valid URL');
      return;
    }
    
    addMediaToGallery(videoUrlInput.trim(), null, 'video');
    setVideoUrlInput('');
  };

  // Helper function to extract YouTube video ID
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/,
      /youtube\.com\/shorts\/([^&\n?#]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Helper function to check if URL is a YouTube video
  const isYouTubeUrl = (url) => {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  // Helper function to check if URL is a video file
  const isVideoFile = (url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
    return videoExtensions.some(ext => url.toLowerCase().includes(ext));
  };

  // Open image/video preview with slider support
  const openImagePreview = (mediaUrl, startIndex = 0) => {
    // Combine main image with media gallery items (including videos)
    const allMedia = [];
    
    // Add main image first if it exists
    if (itemForm.image_url) {
      allMedia.push({ url: itemForm.image_url, type: 'image' });
    }
    
    // Add all media gallery items (images AND videos)
    if (itemForm.media_gallery && itemForm.media_gallery.length > 0) {
      itemForm.media_gallery.forEach(m => allMedia.push({ url: m.url, type: m.type }));
    }
    
    // If no media in form, just show the clicked item
    if (allMedia.length === 0) {
      allMedia.push({ url: mediaUrl, type: 'image' });
    }
    
    // Find the index of the clicked media
    let clickedIndex = allMedia.findIndex(m => m.url === mediaUrl);
    if (clickedIndex === -1) clickedIndex = startIndex;
    
    setPreviewImages(allMedia);
    setPreviewImageIndex(clickedIndex);
    setPreviewImageUrl(allMedia[clickedIndex]?.url || mediaUrl);
    setShowImagePreview(true);
  };

  // Navigate to previous media in preview
  const prevPreviewImage = (e) => {
    e.stopPropagation();
    if (previewImages.length > 1) {
      const newIndex = previewImageIndex === 0 ? previewImages.length - 1 : previewImageIndex - 1;
      setPreviewImageIndex(newIndex);
      setPreviewImageUrl(previewImages[newIndex].url);
    }
  };

  // Navigate to next media in preview
  const nextPreviewImage = (e) => {
    e.stopPropagation();
    if (previewImages.length > 1) {
      const newIndex = previewImageIndex === previewImages.length - 1 ? 0 : previewImageIndex + 1;
      setPreviewImageIndex(newIndex);
      setPreviewImageUrl(previewImages[newIndex].url);
    }
  };


  const handleAIGenerateImage = async () => {
    if (!aiImageInput.name) {
      setAIImageError('Please provide a product name');
      return;
    }
    
    // Validate reference image is provided when mode requires it
    if ((referenceImageMode === 'reference' || referenceImageMode === 'product_cover') && !referenceImageUrl && !referenceImageFile) {
      setAIImageError('Please upload a reference image for the selected mode');
      return;
    }

    setGeneratingImage(true);
    setAIImageError('');
    
    try {
      console.log('🎨 Generating AI image for:', aiImageInput.name);
      console.log('📷 Reference mode:', referenceImageMode);
      console.log('📷 Using reference image:', useReferenceImage ? (referenceImageUrl || 'file upload') : 'none');
      
      // Prepare the request body
      let requestBody = {
        product_name: aiImageInput.name,
        product_description: aiImageInput.description,
        model: selectedImageModel,
        reference_mode: referenceImageMode // 'none', 'reference', 'product_cover'
      };
      
      // If using reference image (either mode)
      if ((referenceImageMode === 'reference' || referenceImageMode === 'product_cover') && (referenceImageUrl || referenceImageFile)) {
        if (referenceImageFile) {
          // Convert file to base64
          const reader = new FileReader();
          const base64Promise = new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
          });
          reader.readAsDataURL(referenceImageFile);
          const base64Data = await base64Promise;
          requestBody.reference_image_base64 = base64Data;
        } else if (referenceImageUrl && !referenceImageUrl.startsWith('blob:')) {
          requestBody.reference_image_url = referenceImageUrl;
        }
      }
      
      const response = await fetch(`${API_BASE_URL}/api/menu-items/generate-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ AI image generated:', data.image_url);
        console.log('🔑 S3 key:', data.s3_key);
        
        if (aiImageTargetGallery) {
          // Add to media gallery instead of replacing main image
          addMediaToGallery(data.image_url, data.s3_key, 'image');
          console.log('📸 Added AI image to gallery:', data.image_url);
          showMessage('success', 'AI image added to gallery! Click "Update Product" to save changes.');
        } else {
          // Update the image URL and s3_key in the form (replace main image)
          const updatedForm = {
            ...itemForm,
            image_url: data.image_url,
            s3_key: data.s3_key
          };
          
          console.log('📸 New image URL:', data.image_url);
          console.log('📝 Updated form data with new image');
          
          // Update the form state with new image
          setItemForm(updatedForm);
          
          // Clear the image file if any
          setImageFile(null);
          
          showMessage('success', 'Image generated! Click "Update Product" to save changes.');
        }
        
        // Clear reference image and reset mode
        clearReferenceImage();
        setUseReferenceImage(false);
        setReferenceImageMode('none');
        setAiImageTargetGallery(false); // Reset target
        
        // Close AI modal only - keep edit modal open for user to review and save
        setShowAIImageModal(false);
        setAIImageInput({ name: '', description: '' });
        setAIImageError('');
        
        // Force a small delay to ensure S3 has processed the image, then refresh S3 images
        setTimeout(() => {
          // If image browser is open, refresh the list
          if (showImageBrowser) {
            fetchS3Images(imageSearchQuery);
          }
        }, 1000);
      } else {
        const errorData = await response.json();
        console.error('❌ Error from backend:', errorData);
        setAIImageError(errorData.detail || 'Failed to generate image. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error generating image:', error);
      setAIImageError('Network error. Please check your connection and try again.');
    } finally {
      setGeneratingImage(false);
    }
  };

  // AI Generate Image Slider Functions
  const fetchSliderReferenceS3Images = async (query = '') => {
    if (!query || !query.trim() || query.trim().length < 3) {
      setSliderReferenceS3Images([]);
      return;
    }
    
    setLoadingSliderReferenceImages(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/menu-items/browse-images?search=${encodeURIComponent(query.trim())}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSliderReferenceS3Images(data.images || []);
      }
    } catch (error) {
      console.error('Error fetching slider reference images:', error);
    } finally {
      setLoadingSliderReferenceImages(false);
    }
  };

  const handleSliderReferenceSearch = () => {
    if (sliderReferenceSearchQuery.trim().length < 3) {
      showMessage('warning', 'Please enter at least 3 characters to search');
      return;
    }
    fetchSliderReferenceS3Images(sliderReferenceSearchQuery);
  };

  const handleSliderReferenceImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const remainingSlots = 5 - sliderReferenceImages.length;
    if (remainingSlots <= 0) {
      showMessage('warning', 'Maximum 5 reference images allowed');
      return;
    }
    
    const filesToAdd = files.slice(0, remainingSlots);
    const newImages = filesToAdd.map(file => ({
      id: `ref_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      url: URL.createObjectURL(file),
      file: file,
      type: 'upload'
    }));
    
    setSliderReferenceImages(prev => [...prev, ...newImages]);
    e.target.value = ''; // Reset input
  };

  const addSliderReferenceFromS3 = (imageUrl) => {
    if (sliderReferenceImages.length >= 5) {
      showMessage('warning', 'Maximum 5 reference images allowed');
      return;
    }
    
    const newImage = {
      id: `ref_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      url: imageUrl,
      file: null,
      type: 's3'
    };
    
    setSliderReferenceImages(prev => [...prev, newImage]);
    setShowSliderReferenceBrowser(false);
  };

  const removeSliderReferenceImage = (imageId) => {
    setSliderReferenceImages(prev => prev.filter(img => img.id !== imageId));
  };

  const clearAllSliderReferenceImages = () => {
    setSliderReferenceImages([]);
  };

  // Face Reference Image Handlers
  const handleFaceReferenceImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const newImage = {
      id: `face_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      url: URL.createObjectURL(file),
      file: file,
      type: 'upload'
    };
    
    setFaceReferenceImage(newImage);
    e.target.value = ''; // Reset input
  };

  const addFaceReferenceFromS3 = (imageUrl) => {
    const newImage = {
      id: `face_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      url: imageUrl,
      file: null,
      type: 's3'
    };
    
    setFaceReferenceImage(newImage);
    setShowFaceReferenceBrowser(false);
  };

  const removeFaceReferenceImage = () => {
    setFaceReferenceImage(null);
  };

  const searchFaceReferenceImages = async () => {
    if (!faceReferenceSearchQuery.trim()) return;
    
    setLoadingFaceReferenceImages(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/menu-items/browse-images?search=${encodeURIComponent(faceReferenceSearchQuery)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFaceReferenceS3Images(data.images || []);
      }
    } catch (error) {
      console.error('Error searching face reference images:', error);
    } finally {
      setLoadingFaceReferenceImages(false);
    }
  };

  const clearAllFaceAndProductImages = () => {
    setFaceReferenceImage(null);
    setSliderReferenceImages([]);
  };

  // Default prompt for fashion/clothing image generation
  const getDefaultFaceProductPrompt = () => {
    return `Generate a photorealistic fashion image showing the model wearing this outfit.

Use the model's facial features, skin tone, and body type from the model reference image.

Use the exact fabric, color, embroidery pattern, draping style, and design details from the clothing reference image.

Show the complete outfit with elegant presentation, professional fashion photography lighting.`;
  };

  const handleGenerateSliderImages = async () => {
    // Validation - require at least clothing images
    if (sliderReferenceImages.length === 0) {
      setSliderError('Please add at least one clothing/outfit reference image');
      return;
    }
    
    if (!sliderPrompt.trim()) {
      setSliderError('Please enter a prompt for image generation');
      return;
    }

    setGeneratingSliderImages(true);
    setSliderError('');
    setSliderGenerationProgress(0);
    setGeneratedSliderImages([]);

    try {
      const generatedImages = [];
      
      // Build the variation prompts for different angles/views
      const variationPrompts = [
        'front view, facing camera directly, elegant pose',
        'side profile view, looking gracefully to the left',
        'three-quarter angle view, slight turn showing outfit detail',
        'back view showing the complete design from behind',
        'close-up detail shot focusing on fabric texture and embroidery',
        'full body shot with natural standing pose',
        'walking pose with dynamic movement',
        'seated elegant pose',
        'artistic angle with dramatic lighting',
        'natural outdoor lighting setting'
      ];
      
      for (let i = 0; i < sliderImageCount; i++) {
        setSliderGenerationProgress(Math.round(((i) / sliderImageCount) * 100));
        
        const variation = variationPrompts[i % variationPrompts.length];
        
        // Build the structured prompt for face + product generation
        let structuredPrompt = sliderPrompt;
        if (faceReferenceImage) {
          structuredPrompt = `${sliderPrompt}\n\nView/Angle: ${variation}. Cinematic lighting, high fashion photography, professional studio quality.`;
        } else {
          structuredPrompt = `${sliderPrompt}. ${variation}. High quality, professional product photography.`;
        }
        
        // Prepare form data
        const formData = new FormData();
        formData.append('prompt', structuredPrompt);
        formData.append('product_name', itemForm.name || 'Product');
        formData.append('variation_index', i.toString());
        
        if (selectedImageModel) {
          formData.append('model', selectedImageModel);
        }
        
        // Add face reference image if provided
        if (faceReferenceImage) {
          if (faceReferenceImage.file) {
            formData.append('face_reference_image', faceReferenceImage.file);
          } else if (faceReferenceImage.url) {
            formData.append('face_reference_image_url', faceReferenceImage.url);
          }
        }
        
        // Add product reference images (cycle through them)
        const productRefImage = sliderReferenceImages[i % sliderReferenceImages.length];
        if (productRefImage.file) {
          formData.append('product_reference_image', productRefImage.file);
        } else if (productRefImage.url) {
          formData.append('product_reference_image_url', productRefImage.url);
        }
        
        // Also send all product images for context (as URLs)
        const allProductUrls = sliderReferenceImages
          .filter(img => img.url)
          .map(img => img.url);
        formData.append('all_product_image_urls', JSON.stringify(allProductUrls));

        const response = await fetch(`${API_BASE_URL}/api/menu-items/generate-face-product-image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          if (data.image_url) {
            generatedImages.push({
              id: `slider_${Date.now()}_${i}`,
              url: data.image_url,
              s3_key: data.s3_key,
              type: 'image',
              order: (itemForm.media_gallery?.length || 0) + i
            });
            setGeneratedSliderImages([...generatedImages]);
          } else if (data.error) {
            console.error(`Image ${i + 1} failed: ${data.error}`);
            // Continue trying other images
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error(`Failed to generate image ${i + 1}:`, errorData.detail || 'Unknown error');
          // If it's a content policy error, show it to the user
          if (errorData.detail && (errorData.detail.includes('celebrity') || errorData.detail.includes('policy') || errorData.detail.includes('no images'))) {
            setSliderError(`Image generation blocked: ${errorData.detail}. Try removing celebrity names or changing the prompt.`);
            break;
          }
        }
        
        // Small delay between generations to avoid rate limiting
        if (i < sliderImageCount - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      setSliderGenerationProgress(100);
      
      if (generatedImages.length > 0) {
        showMessage('success', `Generated ${generatedImages.length} images successfully!`);
      } else if (!sliderError) {
        setSliderError('Failed to generate images. This may be due to content policy restrictions. Try a different prompt without celebrity names.');
      }
      
    } catch (error) {
      console.error('Error generating slider images:', error);
      setSliderError('Network error. Please check your connection and try again.');
    } finally {
      setGeneratingSliderImages(false);
    }
  };

  const addGeneratedImagesToGallery = () => {
    if (generatedSliderImages.length === 0) return;
    
    setItemForm(prev => ({
      ...prev,
      media_gallery: [...(prev.media_gallery || []), ...generatedSliderImages]
    }));
    
    showMessage('success', `Added ${generatedSliderImages.length} images to gallery`);
    
    // Close modal but keep face/clothing/prompt for generating more
    setShowAISliderModal(false);
    setGeneratedSliderImages([]);
    setSliderError('');
    // Keep: sliderPrompt, sliderReferenceImages, faceReferenceImage, sliderImageCount
  };

  const removeGeneratedSliderImage = (imageId) => {
    setGeneratedSliderImages(prev => prev.filter(img => img.id !== imageId));
  };


  const formatPrice = (price) => {
    return `$${parseFloat(price).toFixed(2)} USD`;
  };

  // Access control
  if (!user || !['admin', 'manager', 'owner'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only managers and administrators can access Menu Management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        {/* Message */}
        {message.text && (
          <div className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            'bg-red-50 text-red-800 border border-red-200'
          }`}>
            <div className="flex items-center">
              {message.type === 'success' ? (
                <CheckCircle className="h-5 w-5 mr-2" />
              ) : (
                <AlertCircle className="h-5 w-5 mr-2" />
              )}
              {message.text}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm mb-2">
          {/* Header with Title */}
          <div className="p-4 border-b border-gray-200">
            <h1 className="text-lg font-semibold text-gray-900">Product Management</h1>
          </div>

          {/* Tabs - Integrated with Header Card */}
          <div className="border-b border-gray-200">
            <nav className="flex items-center justify-between -mb-px px-4">
              <div className="flex">
                {menuCategories.map(category => {
                  const isActive = activeMenuCategory === category.id;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setActiveMenuCategory(category.id)}
                      className={`py-3 px-4 text-sm font-medium border-b-2 flex items-center space-x-2 ${
                        isActive
                          ? 'border-theme-blue text-theme-blue'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Package className="h-4 w-4" />
                      <span>{category.name}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowManageCategoriesModal(true)}
                  className="py-3 px-4 text-gray-500 hover:text-theme-blue font-medium text-sm flex items-center space-x-2 transition-colors"
                >
                  <Settings className="h-4 w-4" />
                  <span>Manage Categories</span>
                </button>
                <button
                  onClick={() => setShowCSVSection(!showCSVSection)}
                  className="py-3 px-3 text-gray-500 hover:text-theme-blue font-medium text-sm flex items-center transition-colors rounded-lg hover:bg-gray-50"
                  title="CSV Import/Export"
                >
                  <FileText className="h-4 w-4" />
                  {showCSVSection ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
                </button>
              </div>
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 space-y-3">

            {/* CSV Actions Bar - Collapsible with Smooth Animation */}
            <div 
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                showCSVSection ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setShowCSVImportModal(true)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Import CSV
                  </button>
                  <button
                    onClick={handleCSVExport}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </button>
                  <button
                    onClick={() => setShowCSVFormatModal(true)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    CSV Format
                  </button>
                  <button
                    onClick={handleDeleteAll}
                    className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors flex items-center"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete All Items
                  </button>
                </div>
              </div>
            </div>

            {/* Search and Filter Bar */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
              <div className="flex flex-col sm:flex-row gap-3 items-center">
                <div className="sm:w-80 w-full relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={() => setShowCategoryOverlay(!showCategoryOverlay)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center text-sm font-medium text-gray-700"
                  title="Filter by Category"
                >
                  <Filter className="h-4 w-4" />
                </button>
                <div className="sm:w-48 w-full">
                  <select
                    value={selectedStockStatus}
                    onChange={(e) => setSelectedStockStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="all">All Items</option>
                    <option value="in_stock">In Stock</option>
                    <option value="out_of_stock">Out of Stock</option>
                  </select>
                </div>
                <button
                  onClick={() => {
                    setShowProductCategoriesModal(true);
                    fetchManagedCategories();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  <Settings className="h-4 w-4 mr-2 inline" />
                  Manage Categories
                </button>
                <button
                  onClick={() => {
                    resetItemForm();
                    setShowAddItemModal(true);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors flex items-center"
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </button>
              </div>
            </div>

            {/* Menu Items Grid */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto"></div>
                <p className="text-gray-500 mt-4">Loading menu items...</p>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-12 text-center">
                <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm || selectedCategory
                    ? 'Try adjusting your search or filter criteria'
                    : 'Get started by adding your first product'}
                </p>
                {!searchTerm && !selectedCategory && (
                  <button
                    onClick={() => {
                      resetItemForm();
                      setShowAddItemModal(true);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    Add Product
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {currentItems.map(item => (
              <div
                key={item.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow flex"
              >
                {/* Item Image with Badges - Left Side */}
                <div className="relative w-48 flex-shrink-0 group">
                  {item.image_url ? (
                    <>
                      <img
                        src={`${item.image_url}?t=${item.updated_at || Date.now()}`}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => {
                          // Build preview media array from item's main image + media gallery (including videos)
                          const allMedia = [];
                          if (item.image_url) {
                            allMedia.push({ url: item.image_url, type: 'image' });
                          }
                          if (item.media_gallery && item.media_gallery.length > 0) {
                            item.media_gallery.forEach(m => allMedia.push({ url: m.url, type: m.type }));
                          }
                          if (allMedia.length === 0) {
                            allMedia.push({ url: item.image_url, type: 'image' });
                          }
                          setPreviewImages(allMedia);
                          setPreviewImageIndex(0);
                          setPreviewImageUrl(allMedia[0]?.url || item.image_url);
                          setShowImagePreview(true);
                        }}
                        className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        type="button"
                      >
                        <Eye className="h-8 w-8 text-white" />
                      </button>
                    </>
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <ImageIcon className="h-16 w-16 text-gray-400" />
                    </div>
                  )}
                  
                  {/* Product Type Badge - Top Left */}
                  <span className={`absolute top-2 left-2 px-2 py-1 text-xs font-semibold rounded shadow-sm ${
                    item.is_variable
                      ? 'bg-purple-500 text-white'
                      : 'bg-theme-blue text-white'
                  }`}>
                    {item.is_variable ? 'Variable' : 'Simple'}
                  </span>
                  
                  {/* Component Display Badge - Top Right */}
                  {item.component_display_enabled && (
                    <span className="absolute top-2 right-2 px-2 py-1 text-xs font-semibold rounded shadow-sm bg-green-500 text-white flex items-center gap-1">
                      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z"/>
                      </svg>
                      CMS
                    </span>
                  )}
                  
                  {/* Out of Stock Badge - Bottom Left */}
                  {!item.in_stock && (
                    <span className="absolute bottom-2 left-2 px-2 py-1 text-xs font-semibold rounded shadow-sm bg-red-600 text-white">
                      Out of Stock
                    </span>
                  )}
                  
                  {/* Quantity Badge - Bottom Right (only for simple products and in stock) */}
                  {!item.is_variable && item.quantity !== null && item.in_stock && !item.is_variable && (
                    <span className="absolute bottom-2 right-2 px-2 py-1 text-xs font-medium rounded shadow-sm bg-gray-800 text-white">
                      Qty: {item.quantity}
                    </span>
                  )}
                  
                  {/* Variants Count Badge - Bottom Right (for variable products and in stock) */}
                  {item.is_variable && item.variants && item.variants.length > 0 && item.in_stock && (
                    <span className="absolute bottom-2 right-2 px-2 py-1 text-xs font-medium rounded shadow-sm bg-purple-600 text-white">
                      {item.variants.length} Variant{item.variants.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {/* Item Details - Right Side */}
                <div className="flex-1 p-3 flex flex-col">
                  <h3 className="font-semibold text-gray-900 text-sm mb-1.5">{item.name}</h3>

                  <p className="text-gray-600 text-xs mb-2 line-clamp-3 leading-relaxed flex-1">{item.description || 'No description'}</p>

                  {/* Price Display */}
                  <div className="mb-2">
                    {item.is_variable && item.variants && item.variants.length > 0 ? (
                      <span className="text-sm font-bold text-gray-900">
                        {formatPrice(Math.min(...item.variants.map(v => v.price)))} - {formatPrice(Math.max(...item.variants.map(v => v.price)))}
                      </span>
                    ) : (
                      <span className="text-sm font-bold text-gray-900">{formatPrice(item.price)}</span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-2">
                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">{item.category}</span>
                    
                    {/* Show weight/size only for simple products */}
                    {!item.is_variable && item.weight && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">{item.weight}</span>
                    )}
                    {!item.is_variable && item.size && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">{item.size}</span>
                    )}
                    
                    {/* For variable products, show variant names instead */}
                    {item.is_variable && item.variants && item.variants.length > 0 && (
                      <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded line-clamp-1">
                        {item.variants.map(v => v.name).join(', ')}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-auto">
                    <button
                      onClick={() => openEditModal(item)}
                      className="flex-1 px-2 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center"
                    >
                      <Edit className="h-3.5 w-3.5 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => toggleStockStatus(item.id, item.in_stock)}
                      className={`px-2 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center justify-center ${
                        item.in_stock 
                          ? 'text-green-700 bg-green-50 hover:bg-green-100' 
                          : 'text-red-700 bg-red-50 hover:bg-red-100'
                      }`}
                      title={item.in_stock ? 'Mark as Out of Stock' : 'Mark as In Stock'}
                    >
                      {item.in_stock ? (
                        <>
                          <svg className="h-3.5 w-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          In Stock
                        </>
                      ) : (
                        <>
                          <svg className="h-3.5 w-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Out of Stock
                        </>
                      )}
                    </button>
                  </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-700">
                    <span>
                      Showing {startIndex + 1} to {Math.min(endIndex, filteredItems.length)} of {filteredItems.length} items
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    <div className="flex space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
                        <button
                          key={pageNumber}
                          onClick={() => goToPage(pageNumber)}
                          className={`px-3 py-2 text-sm font-medium rounded-lg ${
                            currentPage === pageNumber
                              ? 'text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                          style={currentPage === pageNumber ? { backgroundColor: 'var(--theme-primary)' } : {}}
                        >
                          {pageNumber}
                        </button>
                      ))}
                    </div>
                    
                    <button
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
          </div>
        </div>

        {/* Manage Categories Modal */}
        {showManageCategoriesModal && ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-xl w-full max-h-[85vh] overflow-hidden flex flex-col">
              <div className="px-4 py-3 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <div className="flex items-center">
                  <Settings className="h-4 w-4 mr-2 text-white" />
                  <h3 className="text-base font-semibold text-white">Manage Menu Categories</h3>
                </div>
                <button
                  onClick={() => {
                    setShowManageCategoriesModal(false);
                    setCategoryForm({ name: '', description: '' });
                  }}
                  className="text-white hover:text-gray-200"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {/* Existing Categories List */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Existing Categories</h4>
                  {menuCategories.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-lg">
                      <Package className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">No categories yet. Add your first category below.</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {menuCategories.map(category => (
                        <div
                          key={category.id}
                          className="flex items-center justify-between p-2.5 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center flex-1">
                            <Package className="h-4 w-4 text-gray-400 mr-2" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {category.name}
                                <span className="ml-2 text-xs text-gray-500">
                                  ({allMenuItems.filter(item => item.menu_category_id === category.id).length})
                                </span>
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteCategory(category.id, category.name)}
                            className="ml-3 p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Delete category"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Add New Category Form */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Add New Category</h4>
                  {/* Form Section with Grey Background */}
                  <div className="bg-gray-50 rounded-lg p-3 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Category Name *
                      </label>
                      <input
                        type="text"
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                        placeholder="e.g., Pet Foods, Pet Vaccines"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Description
                      </label>
                      <textarea
                        value={categoryForm.description}
                        onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                        placeholder="Optional description"
                        rows="2"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      />
                    </div>

                    <button
                      onClick={() => {
                        handleAddCategory();
                        setCategoryForm({ name: '', description: '' });
                      }}
                      className="w-full px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors flex items-center justify-center"
                      style={{ backgroundColor: 'var(--theme-primary)' }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Category
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Add/Edit Item Modal */}
        {(showAddItemModal || showEditItemModal) && ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8">
              <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <h3 className="text-lg font-semibold text-white">
                  {showEditItemModal ? 'Edit Product' : 'Add Product'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddItemModal(false);
                    setShowEditItemModal(false);
                    resetItemForm();
                  }}
                  className="text-white hover:text-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Item Name *
                    </label>
                    <input
                      type="text"
                      value={itemForm.name}
                      onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                      placeholder="Enter item name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={itemForm.category}
                      onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="">Select a category</option>
                      {managedCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    {managedCategories.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        No categories available. Use "Manage Categories" to add categories.
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setAIGenerateInput({ name: itemForm.name || '', description: '' });
                        setShowAIGenerateModal(true);
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
                      style={{ backgroundColor: 'rgb(147, 51, 234)', color: 'white' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(126, 34, 206)'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(147, 51, 234)'}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      AI Generate
                    </button>
                  </div>
                  <textarea
                    value={itemForm.description}
                    onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                    placeholder="Enter description (optional)"
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Image
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    {imageFile || itemForm.image_url ? (
                      <div className="space-y-3">
                        {imageFile && (
                          <div className="relative inline-block group">
                            <img
                              src={URL.createObjectURL(imageFile)}
                              alt="Preview"
                              className="w-40 h-40 object-contain mx-auto rounded bg-gray-50"
                            />
                            <button
                              onClick={() => openImagePreview(URL.createObjectURL(imageFile), 0)}
                              className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded"
                              type="button"
                            >
                              <Eye className="h-8 w-8 text-white" />
                            </button>
                          </div>
                        )}
                        {!imageFile && itemForm.image_url && (
                          <div className="relative inline-block group">
                            <img
                              key={itemForm.image_url}
                              src={itemForm.image_url}
                              alt="Current"
                              className="w-40 h-40 object-contain mx-auto rounded bg-gray-50"
                              onError={(e) => {
                                console.error('Image failed to load:', itemForm.image_url);
                                e.target.style.display = 'none';
                              }}
                              onLoad={() => {
                                console.log('✅ Image loaded successfully:', itemForm.image_url);
                              }}
                            />
                            <button
                              onClick={() => openImagePreview(itemForm.image_url, 0)}
                              className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded"
                              type="button"
                            >
                              <Eye className="h-8 w-8 text-white" />
                            </button>
                          </div>
                        )}
                        <div className="flex gap-2 justify-center flex-wrap">
                          <button
                            onClick={() => {
                              setItemForm({ ...itemForm, image_url: '' });
                              setImageFile(null);
                            }}
                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                          >
                            Remove Image
                          </button>
                          <button
                            onClick={() => {
                              setShowImageBrowser(true);
                              setImageSearchQuery('');
                              setS3Images([]);
                              setHasSearched(false);
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors"
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            Browse Images
                          </button>
                          <button
                            onClick={() => {
                              setAiImageTargetGallery(false); // Replace main image
                              setAIImageInput({
                                name: itemForm.name || '',
                                description: itemForm.description || ''
                              });
                              setShowAIImageModal(true);
                              setAIImageError('');
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors flex items-center gap-1"
                            style={{ backgroundColor: 'rgb(147, 51, 234)' }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            AI Generate Image
                          </button>
                          <button
                            onClick={() => {
                              setShowAISliderModal(true);
                              // Only clear error and generated images, keep face/clothing/prompt for reuse
                              setSliderError('');
                              setGeneratedSliderImages([]);
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors flex items-center gap-1"
                            style={{ backgroundColor: 'rgb(236, 72, 153)' }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            AI Fashion Images
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-600 mb-2">Upload product image</p>
                        <div className="flex gap-2 justify-center flex-wrap">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setImageFile(e.target.files[0])}
                            className="hidden"
                            id="image-upload"
                          />
                          <label
                            htmlFor="image-upload"
                            className="inline-block px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                          >
                            Upload Image
                          </label>
                          <button
                            onClick={() => {
                              setShowImageBrowser(true);
                              setImageSearchQuery('');
                              setS3Images([]);
                              setHasSearched(false);
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            Browse Images
                          </button>
                          <button
                            onClick={() => {
                              setAiImageTargetGallery(false); // Replace main image
                              setAIImageInput({
                                name: itemForm.name || '',
                                description: itemForm.description || ''
                              });
                              setShowAIImageModal(true);
                              setAIImageError('');
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 flex items-center gap-1"
                            style={{ backgroundColor: 'rgb(147, 51, 234)' }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            AI Generate
                          </button>
                          <button
                            onClick={() => {
                              setShowAISliderModal(true);
                              // Only clear error and generated images, keep face/clothing/prompt for reuse
                              setSliderError('');
                              setGeneratedSliderImages([]);
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 flex items-center gap-1"
                            style={{ backgroundColor: 'rgb(236, 72, 153)' }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            AI Fashion
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Optional: Add an image to make your product more appealing to customers. Max 5MB.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Media Gallery Section */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">
                      Media Gallery (Additional Images/Videos)
                    </label>
                    <span className="text-xs text-gray-500">{itemForm.media_gallery?.length || 0} items</span>
                  </div>
                  
                  {/* Gallery Items */}
                  {itemForm.media_gallery && itemForm.media_gallery.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      {itemForm.media_gallery.map((media, index) => (
                        <div key={media.id} className="relative group border rounded-lg overflow-hidden bg-white">
                          {media.type === 'video' ? (
                            <div 
                              className="w-full h-20 bg-gray-100 flex items-center justify-center cursor-pointer relative"
                              onClick={() => openImagePreview(media.url, index + 1)}
                            >
                              {isYouTubeUrl(media.url) ? (
                                // YouTube thumbnail
                                <img 
                                  src={`https://img.youtube.com/vi/${getYouTubeVideoId(media.url)}/mqdefault.jpg`}
                                  alt="Video thumbnail"
                                  className="w-full h-full object-contain bg-gray-100"
                                />
                              ) : (
                                // S3/Local video - show first frame using video element
                                <video 
                                  src={media.url}
                                  className="w-full h-full object-contain bg-gray-100"
                                  preload="metadata"
                                  muted
                                  playsInline
                                  onLoadedData={(e) => {
                                    // Seek to first frame to show thumbnail
                                    e.target.currentTime = 0.1;
                                  }}
                                  onError={(e) => {
                                    // If video fails to load, hide video and show fallback
                                    e.target.style.display = 'none';
                                    e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                                  }}
                                />
                              )}
                              {/* Fallback icon for failed video load */}
                              <div className="absolute inset-0 items-center justify-center bg-gray-200 hidden" style={{display: 'none'}}>
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </div>
                              {/* Play icon overlay */}
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-8 h-8 bg-black bg-opacity-60 rounded-full flex items-center justify-center">
                                  <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <img 
                              src={media.url} 
                              alt="" 
                              className="w-full h-20 object-contain bg-gray-100 cursor-pointer" 
                              onClick={() => openImagePreview(media.url, index + 1)}
                            />
                          )}
                          
                          {/* Overlay controls */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                            <button
                              type="button"
                              onClick={() => openImagePreview(media.url, index + 1)}
                              className="p-1 bg-white rounded text-gray-700 hover:bg-gray-100"
                              title="Preview"
                            >
                              <Eye className="w-3 h-3" />
                            </button>
                            {index > 0 && (
                              <button
                                type="button"
                                onClick={() => moveMediaInGallery(media.id, 'up')}
                                className="p-1 bg-white rounded text-gray-700 hover:bg-gray-100"
                                title="Move left"
                              >
                                <ChevronLeft className="w-3 h-3" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeMediaFromGallery(media.id)}
                              className="p-1 bg-red-500 rounded text-white hover:bg-red-600"
                              title="Remove"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            {index < itemForm.media_gallery.length - 1 && (
                              <button
                                type="button"
                                onClick={() => moveMediaInGallery(media.id, 'down')}
                                className="p-1 bg-white rounded text-gray-700 hover:bg-gray-100"
                                title="Move right"
                              >
                                <ChevronRight className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          
                          {/* Type badge */}
                          <div className="absolute top-1 left-1">
                            <span className={`text-[10px] px-1 py-0.5 rounded ${media.type === 'video' ? 'bg-purple-500 text-white' : 'bg-theme-blue text-white'}`}>
                              {media.type === 'video' ? 'Video' : 'Image'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add Media Buttons */}
                  <div className="space-y-3">
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => {
                          setShowMediaGalleryBrowser(true);
                          setMediaGallerySearchQuery('');
                          setMediaGalleryS3Images([]);
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-white rounded-lg hover:opacity-90"
                        style={{ backgroundColor: 'var(--theme-primary)' }}
                      >
                        + Add Image from S3
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setAiImageTargetGallery(true); // Set to add to gallery
                          setAIImageInput({ name: itemForm.name || '', description: itemForm.description || '' });
                          setShowAIImageModal(true);
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
                      >
                        + AI Generate Image
                      </button>
                      <label className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                        + Upload Image
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              // Upload to S3 and add to gallery
                              setUploadingImage(true);
                              try {
                                const formData = new FormData();
                                formData.append('file', file);
                                formData.append('folder', 'images/Menu Items');
                                
                                const response = await fetch(`${API_BASE_URL}/api/s3/upload`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}` },
                                  body: formData
                                });
                                
                                if (response.ok) {
                                  const data = await response.json();
                                  addMediaToGallery(data.url, data.key, 'image');
                                } else {
                                  showMessage('error', 'Failed to upload image');
                                }
                              } catch (error) {
                                console.error('Upload error:', error);
                                showMessage('error', 'Failed to upload image');
                              } finally {
                                setUploadingImage(false);
                              }
                            }
                            e.target.value = '';
                          }}
                          className="hidden"
                        />
                      </label>
                    </div>
                    
                    {/* Video URL Input */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={videoUrlInput}
                        onChange={(e) => setVideoUrlInput(e.target.value)}
                        placeholder="Enter video URL (YouTube, Vimeo, etc.)"
                        className="flex-1 px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addVideoUrl();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={addVideoUrl}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
                      >
                        Add Video
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-2">
                    Add multiple images or videos for a product slider. Drag items to reorder.
                  </p>
                </div>

                {/* Variable Product Toggle */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">
                      Variable Product
                    </label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={itemForm.is_variable}
                        onChange={(e) => {
                          const isVariable = e.target.checked;
                          setItemForm({ 
                            ...itemForm, 
                            is_variable: isVariable,
                            variants: isVariable ? [{ name: '', price: '', stock: '' }] : []
                          });
                        }}
                        className="sr-only peer"
                      />
                      <div 
                        className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                        style={itemForm.is_variable ? { 
                          backgroundColor: 'var(--theme-primary)',
                          boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                        } : {}}
                      ></div>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">
                    Enable this for products with multiple sizes or weights (e.g., Small/Medium/Large, 1lb/2lb/5lb)
                  </p>
                </div>

                {/* Variation Type Selection */}
                {itemForm.is_variable && (
                  <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-white">
                    {/* Variant Type Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Variant Type Name *</label>
                      <input
                        type="text"
                        placeholder="e.g., Size, Weight, Package"
                        value={variantType}
                        onChange={(e) => setVariantType(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">This describes what varies (e.g., "Size" for Small/Medium/Large options)</p>
                    </div>

                    {/* Variants List */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">Variants</label>
                      {(itemForm.variants || []).map((variant, vIdx) => (
                        <div key={vIdx} className="flex gap-2 items-center bg-gray-50 p-3 rounded-lg">
                          <input
                            type="text"
                            placeholder="Variant name (e.g., Small, Medium, Large)"
                            value={variant.name}
                            onChange={(e) => {
                              const newVariants = [...(itemForm.variants || [])];
                              newVariants[vIdx].name = e.target.value;
                              setItemForm({ ...itemForm, variants: newVariants });
                            }}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                          />
                          <input
                            type="number"
                            placeholder="Price"
                            step="0.01"
                            value={variant.price || ''}
                            onChange={(e) => {
                              const newVariants = [...(itemForm.variants || [])];
                              newVariants[vIdx].price = e.target.value;
                              setItemForm({ ...itemForm, variants: newVariants });
                            }}
                            className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                          />
                          <input
                            type="number"
                            placeholder="Stock"
                            value={variant.stock || ''}
                            onChange={(e) => {
                              const newVariants = [...(itemForm.variants || [])];
                              newVariants[vIdx].stock = e.target.value;
                              setItemForm({ ...itemForm, variants: newVariants });
                            }}
                            className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                          />
                          <button
                            onClick={() => {
                              const newVariants = (itemForm.variants || []).filter((_, i) => i !== vIdx);
                              setItemForm({ ...itemForm, variants: newVariants });
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete variant"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          setItemForm({
                            ...itemForm,
                            variants: [...(itemForm.variants || []), { name: '', price: '', stock: '' }]
                          });
                        }}
                        className="w-full px-4 py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-400 hover:text-theme-blue transition-colors flex items-center justify-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add Variant
                      </button>
                    </div>
                  </div>
                )}

                {/* Simple Product Fields (only when not variable) */}
                {!itemForm.is_variable && (
                  <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-white">
                    {/* Row 1: Weight/Size Selection */}
                    <div>
                      {/* Selection Buttons */}
                      <div className="flex gap-2 mb-2">
                        <button
                          type="button"
                          onClick={() => {
                            setItemForm({ ...itemForm, variation_type: 'size', weight: '' });
                          }}
                          className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                            itemForm.variation_type === 'size'
                              ? 'bg-[rgb(41,173,211)] text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          Size
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setItemForm({ ...itemForm, variation_type: 'weight', size: '' });
                          }}
                          className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                            itemForm.variation_type === 'weight'
                              ? 'bg-[rgb(41,173,211)] text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          Weight
                        </button>
                      </div>

                      {/* Input Field Based on Selection */}
                      {itemForm.variation_type === 'weight' && (
                        <div className="relative">
                          <input
                            type="text"
                            value={itemForm.weight}
                            onChange={(e) => setItemForm({ ...itemForm, weight: e.target.value })}
                            placeholder="e.g., 500g, 1kg, 2lbs"
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                          />
                        </div>
                      )}
                      {itemForm.variation_type === 'size' && (
                        <div className="relative">
                          <input
                            type="text"
                            value={itemForm.size}
                            onChange={(e) => setItemForm({ ...itemForm, size: e.target.value })}
                            placeholder="e.g., Small, Medium, Large"
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                          />
                        </div>
                      )}
                      {!itemForm.variation_type && (
                        <div className="text-xs text-gray-500 italic py-2.5 text-center">
                          Select size or weight above
                        </div>
                      )}
                    </div>

                    {/* Row 2: Price and Quantity */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Price Field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Price * <span className="text-gray-500 font-normal">(USD)</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                          <input
                            type="number"
                            step="0.01"
                            value={itemForm.price}
                            onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                            placeholder="0.00"
                            className="w-full pl-8 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                          />
                        </div>
                      </div>

                      {/* Quantity Field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Quantity <span className="text-gray-500 font-normal text-xs">(Optional)</span>
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={itemForm.quantity}
                            onChange={(e) => setItemForm({ ...itemForm, quantity: e.target.value })}
                            placeholder="e.g., 10"
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Stock Status Toggle */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <div>
                        <label className="text-sm font-medium text-gray-900">
                          Stock Status
                        </label>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Mark if this product is currently available
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={itemForm.in_stock}
                          onChange={(e) => setItemForm({ ...itemForm, in_stock: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div 
                          className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                          style={itemForm.in_stock ? { 
                            backgroundColor: 'var(--theme-primary)',
                            boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                          } : {}}
                        ></div>
                        <span className="ml-3 text-sm font-medium text-gray-700">
                          {itemForm.in_stock ? 'In Stock' : 'Out of Stock'}
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Product Options Section */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Product Options
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Add customizable options like toppings, sizes, or add-ons
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={itemForm.has_options}
                        onChange={(e) => {
                          const hasOptions = e.target.checked;
                          setItemForm({ 
                            ...itemForm, 
                            has_options: hasOptions,
                            options: hasOptions ? [] : []
                          });
                        }}
                        className="sr-only peer"
                      />
                      <div 
                        className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                        style={itemForm.has_options ? { 
                          backgroundColor: 'var(--theme-primary)',
                          boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                        } : {}}
                      ></div>
                    </label>
                  </div>

                  {itemForm.has_options && (
                    <div className="space-y-4 mt-4">
                      {/* Options List */}
                      {itemForm.options && itemForm.options.length > 0 && (
                        <div className="space-y-4">
                          {itemForm.options.map((option, optionIndex) => (
                            <div key={optionIndex} className="border border-gray-300 rounded-lg p-4 bg-white">
                              {/* Option Header */}
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold text-gray-700">
                                  Option {optionIndex + 1}
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newOptions = itemForm.options.filter((_, i) => i !== optionIndex);
                                    setItemForm({ ...itemForm, options: newOptions });
                                  }}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete option"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>

                              {/* Option Name */}
                              <div className="mb-3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Option Name *
                                </label>
                                <input
                                  type="text"
                                  value={option.option_name || ''}
                                  onChange={(e) => {
                                    const newOptions = [...itemForm.options];
                                    newOptions[optionIndex].option_name = e.target.value;
                                    setItemForm({ ...itemForm, options: newOptions });
                                  }}
                                  placeholder="e.g., Add Extra Chicken or Spicy Level"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                              </div>

                              {/* Option Type and Required Toggle */}
                              <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Option Type *
                                  </label>
                                  <select
                                    value={option.option_type || 'single'}
                                    onChange={(e) => {
                                      const newOptions = [...itemForm.options];
                                      newOptions[optionIndex].option_type = e.target.value;
                                      // Reset fields when switching type
                                      if (e.target.value === 'single') {
                                        newOptions[optionIndex].choices = undefined;
                                      } else {
                                        newOptions[optionIndex].price = undefined;
                                        newOptions[optionIndex].choices = [{ choice_id: Date.now().toString(), choice_name: '', price: '' }];
                                      }
                                      setItemForm({ ...itemForm, options: newOptions });
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                  >
                                    <option value="single">Single (Add-on)</option>
                                    <option value="dropdown">Dropdown (Choices)</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Customer Selection
                                  </label>
                                  <div className="flex items-center h-10 px-3 border border-gray-200 rounded-lg bg-gray-50">
                                    <label className="flex items-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={option.is_required || false}
                                        onChange={(e) => {
                                          const newOptions = [...itemForm.options];
                                          newOptions[optionIndex].is_required = e.target.checked;
                                          setItemForm({ ...itemForm, options: newOptions });
                                        }}
                                        className="mr-2"
                                      />
                                      <span className="text-xs text-gray-700">Required</span>
                                    </label>
                                  </div>
                                </div>
                              </div>

                              {/* Single Option Price */}
                              {option.option_type === 'single' && (
                                <div className="mb-3">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Price (Optional)
                                  </label>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={option.price || ''}
                                      onChange={(e) => {
                                        const newOptions = [...itemForm.options];
                                        newOptions[optionIndex].price = e.target.value;
                                        setItemForm({ ...itemForm, options: newOptions });
                                      }}
                                      placeholder="0.00"
                                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                    />
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">
                                    e.g., +$3.00 for "Add Extra Chicken"
                                  </p>
                                </div>
                              )}

                              {/* Dropdown Choices */}
                              {option.option_type === 'dropdown' && (
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <label className="block text-xs font-medium text-gray-700">
                                      Choices * (Max 5)
                                    </label>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const currentChoices = option.choices || [];
                                        if (currentChoices.length >= 5) {
                                          alert('Maximum 5 choices allowed per dropdown option');
                                          return;
                                        }
                                        const newOptions = [...itemForm.options];
                                        newOptions[optionIndex].choices = [
                                          ...currentChoices,
                                          { choice_id: Date.now().toString(), choice_name: '', price: '' }
                                        ];
                                        setItemForm({ ...itemForm, options: newOptions });
                                      }}
                                      disabled={(option.choices || []).length >= 5}
                                      className="text-xs px-2 py-1 rounded-md text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      style={{ backgroundColor: 'var(--theme-primary)' }}
                                    >
                                      + Add Choice
                                    </button>
                                  </div>

                                  <div className="space-y-2">
                                    {(option.choices || []).map((choice, choiceIndex) => (
                                      <div key={choice.choice_id || choiceIndex} className="flex gap-2 items-start">
                                        <div className="flex-1">
                                          <input
                                            type="text"
                                            value={choice.choice_name || ''}
                                            onChange={(e) => {
                                              const newOptions = [...itemForm.options];
                                              newOptions[optionIndex].choices[choiceIndex].choice_name = e.target.value;
                                              setItemForm({ ...itemForm, options: newOptions });
                                            }}
                                            placeholder="e.g., Mild, Medium, Hot"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                          />
                                        </div>
                                        <div className="w-32">
                                          <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                            <input
                                              type="number"
                                              step="0.01"
                                              value={choice.price || ''}
                                              onChange={(e) => {
                                                const newOptions = [...itemForm.options];
                                                newOptions[optionIndex].choices[choiceIndex].price = e.target.value;
                                                setItemForm({ ...itemForm, options: newOptions });
                                              }}
                                              placeholder="0.00"
                                              className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                            />
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newOptions = [...itemForm.options];
                                            newOptions[optionIndex].choices = newOptions[optionIndex].choices.filter((_, i) => i !== choiceIndex);
                                            setItemForm({ ...itemForm, options: newOptions });
                                          }}
                                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                          title="Delete choice"
                                        >
                                          <X className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Price is optional for each choice (e.g., "Add Wings +$2.00")
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Option Button */}
                      <button
                        type="button"
                        onClick={() => {
                          const newOption = {
                            option_id: Date.now().toString(),
                            option_name: '',
                            option_type: 'single',
                            is_required: false,
                            price: ''
                          };
                          setItemForm({
                            ...itemForm,
                            options: [...(itemForm.options || []), newOption]
                          });
                        }}
                        className="w-full px-4 py-2.5 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors flex items-center justify-center gap-2"
                        style={{ backgroundColor: 'var(--theme-primary)' }}
                      >
                        <Plus className="h-4 w-4" />
                        Add Option
                      </button>
                    </div>
                  )}
                </div>

                {/* Component Display Section */}
                <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <span className="text-lg">📦</span>
                        Enable Component Display
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Show this product in CMS Product Listing components
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={itemForm.component_display_enabled}
                        onChange={(e) => setItemForm({ ...itemForm, component_display_enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div 
                        className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                        style={itemForm.component_display_enabled ? { 
                          backgroundColor: 'var(--theme-primary)',
                          boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                        } : {}}
                      ></div>
                    </label>
                  </div>
                </div>

                {/* Bargain Mode Section */}
                <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <span className="text-lg">💰</span>
                        Enable Bargain Mode
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Allow customers to make price offers on this product
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={itemForm.bargain_enabled}
                        onChange={(e) => {
                          setItemForm({ 
                            ...itemForm, 
                            bargain_enabled: e.target.checked,
                            min_bargain_price: e.target.checked ? itemForm.min_bargain_price : ''
                          });
                        }}
                        className="sr-only peer"
                      />
                      <div 
                        className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                        style={itemForm.bargain_enabled ? { 
                          backgroundColor: 'rgb(234, 88, 12)',
                          boxShadow: '0 0 0 4px rgba(234, 88, 12, 0.2)' 
                        } : {}}
                      ></div>
                    </label>
                  </div>

                  {itemForm.bargain_enabled && (
                    <div className="mt-4 space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Minimum Bargain Price *
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={itemForm.min_bargain_price}
                            onChange={(e) => setItemForm({ ...itemForm, min_bargain_price: e.target.value })}
                            placeholder="Enter minimum acceptable price"
                            className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Customers cannot offer less than this amount. Leave empty to accept any offer.
                        </p>
                      </div>
                      <div className="bg-white border border-orange-200 rounded-lg p-3">
                        <p className="text-xs text-orange-700">
                          <strong>How it works:</strong> A "Bargain Price" button will appear next to "Add to Cart" on the public store. 
                          Customers can submit their price offers, and you can review them in the Manage Bargains page.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                {/* Delete button (only in edit mode) */}
                <div>
                  {showEditItemModal && editingItem && (
                    <button
                      onClick={() => handleDeleteItem(editingItem.id)}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Product
                    </button>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowAddItemModal(false);
                      setShowEditItemModal(false);
                      resetItemForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={showEditItemModal ? handleUpdateItem : handleAddItem}
                    disabled={uploadingImage}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    {uploadingImage ? 'Uploading...' : showEditItemModal ? 'Update Product' : 'Add Product'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* CSV Import Modal */}
        {showCSVImportModal && ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Import CSV</h3>
                <button
                  onClick={() => {
                    setShowCSVImportModal(false);
                    setCsvFile(null);
                    setCsvImportResult(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select CSV File
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Upload a CSV file with your menu items. Make sure it follows the correct format.
                  </p>
                </div>

                {csvImportResult && (
                  <div className={`p-4 rounded-lg mb-4 ${
                    csvImportResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}>
                    <p className={`font-medium ${csvImportResult.success ? 'text-green-800' : 'text-red-800'}`}>
                      {csvImportResult.message}
                    </p>
                    {csvImportResult.errors.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-red-800 mb-2">Errors:</p>
                        <div className="max-h-40 overflow-y-auto">
                          {csvImportResult.errors.map((error, idx) => (
                            <p key={idx} className="text-sm text-red-700 mb-1">• {error}</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Click "CSV Format" button to see the required format and example data.
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowCSVImportModal(false);
                    setCsvFile(null);
                    setCsvImportResult(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={handleCSVImport}
                  disabled={!csvFile || loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-theme-blue rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Importing...' : 'Import CSV'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* CSV Format Modal */}
        {showCSVFormatModal && ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              {/* Sticky Header */}
              <div className="px-6 py-4 flex items-center justify-between rounded-t-xl flex-shrink-0" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <div className="flex items-center">
                  <FileText className="h-6 w-6 text-white mr-2" />
                  <h3 className="text-lg font-semibold text-white">CSV Import Format Guide</h3>
                </div>
                <button
                  onClick={() => setShowCSVFormatModal(false)}
                  className="text-white hover:text-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="p-6 space-y-6 overflow-y-auto flex-1">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-medium">
                    Your CSV file must include these exact column headers (case-insensitive):
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Column Headers:</h4>
                  
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                    <p className="font-medium text-gray-900 mb-2">Required Fields:</p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex justify-between">
                        <span className="font-mono bg-white px-2 py-1 rounded">name</span>
                        <span className="text-gray-600">Product name</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="font-mono bg-white px-2 py-1 rounded">category</span>
                        <span className="text-gray-600">Menu category (matches tab name)</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="font-mono bg-white px-2 py-1 rounded">price</span>
                        <span className="text-gray-600">Price as number (required for simple products)</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                    <p className="font-medium text-gray-900 mb-2">Optional Fields - Simple Products:</p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex justify-between">
                        <span className="font-mono bg-white px-2 py-1 rounded">description</span>
                        <span className="text-gray-600">Item description</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="font-mono bg-white px-2 py-1 rounded">product_category</span>
                        <span className="text-gray-600">Product sub-category</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="font-mono bg-white px-2 py-1 rounded">in_stock</span>
                        <span className="text-gray-600">true/false, 1/0, yes/no (default: true)</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="font-mono bg-white px-2 py-1 rounded">quantity</span>
                        <span className="text-gray-600">Available quantity (number)</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="font-mono bg-white px-2 py-1 rounded">variation_type</span>
                        <span className="text-gray-600">"size" or "weight"</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="font-mono bg-white px-2 py-1 rounded">weight</span>
                        <span className="text-gray-600">e.g., "500g", "1kg" (if variation_type=weight)</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="font-mono bg-white px-2 py-1 rounded">size</span>
                        <span className="text-gray-600">e.g., "Small", "Large" (if variation_type=size)</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 border-l-4 border-purple-400 p-4">
                    <p className="font-medium text-gray-900 mb-2">Variable Products:</p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex justify-between">
                        <span className="font-mono bg-white px-2 py-1 rounded">is_variable</span>
                        <span className="text-gray-600">true/false (set true for variable products)</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="font-mono bg-white px-2 py-1 rounded">variation_type</span>
                        <span className="text-gray-600">"size" or "weight"</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="font-mono bg-white px-2 py-1 rounded">variants</span>
                        <span className="text-gray-600">JSON array: [{"{"}"name":"Small","price":10,"stock":5{"}"}]</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Example CSV (Simple Products):</h4>
                  <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <pre className="text-sm font-mono">
{`name,description,price,category,product_category,in_stock,quantity,variation_type,weight,size
"Premium Dog Food","Chicken & Rice formula",45.99,"Pet Food","Dog Food",true,10,"weight","5kg",""
"Cat Treats","Salmon flavor snacks",8.99,"Pet Food","Cat Treats",true,50,"size","","Small"
"Dog Collar","Adjustable nylon collar",12.99,"Accessories","Collars",true,25,"size","","Medium"`}
                    </pre>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 mt-4">Example CSV (Variable Products):</h4>
                  <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <pre className="text-sm font-mono">
{`name,description,category,product_category,is_variable,variation_type,variants
"Pet Shampoo","Gentle formula for all breeds","Pet Care","Grooming",true,"size","[{\\"name\\":\\"Small\\",\\"price\\":9.99,\\"stock\\":20},{\\"name\\":\\"Large\\",\\"price\\":16.99,\\"stock\\":15}]"
"Dog Food Premium","High protein formula","Pet Food","Dog Food",true,"weight","[{\\"name\\":\\"2kg\\",\\"price\\":25.99,\\"stock\\":30},{\\"name\\":\\"5kg\\",\\"price\\":55.99,\\"stock\\":20}]"`}
                    </pre>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="font-medium text-gray-900 mb-2">Important Notes:</p>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Use double quotes around text values that contain commas</li>
                    <li><strong>category</strong> must match your menu category tab name (e.g., "Pet Food")</li>
                    <li><strong>product_category</strong> is optional sub-category within the menu category</li>
                    <li>For simple products: set price, and optionally weight OR size based on variation_type</li>
                    <li>For variable products: set is_variable=true, omit price/quantity, provide variants as JSON</li>
                    <li>Variants JSON must escape quotes with backslash: {`{\"name\":\"Small\",\"price\":10}`}</li>
                    <li>in_stock accepts: true/false, 1/0, yes/no (default: true)</li>
                  </ul>
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end flex-shrink-0">
                <button
                  onClick={() => setShowCSVFormatModal(false)}
                  className="px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* Delete Category Confirmation Modal */}
      {showDeleteConfirmModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="ml-3 text-lg font-semibold text-gray-900">Delete Menu Category</h3>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              {deleteError ? (
                <div className="space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-red-800">Cannot Delete Category</h4>
                        <p className="mt-1 text-sm text-red-700">{deleteError}</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Please delete or move all menu items from this category before deleting the category itself.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-700">
                    Are you sure you want to delete the category{' '}
                    <span className="font-semibold text-gray-900">"{deleteCategoryData?.name}"</span>?
                  </p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <p className="ml-2 text-sm text-yellow-700">
                        This action cannot be undone. The category must be empty (no menu items) to be deleted.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={cancelDeleteCategory}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {deleteError ? 'Close' : 'Cancel'}
              </button>
              {!deleteError && (
                <button
                  onClick={confirmDeleteCategory}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Category
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Image Browser Modal */}
      {showImageBrowser && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <h3 className="text-lg font-semibold text-white">Browse Images</h3>
              <button
                onClick={() => setShowImageBrowser(false)}
                className="text-white hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Search Box */}
              <div className="mb-6">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={imageSearchQuery}
                    onChange={(e) => setImageSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && imageSearchQuery.trim().length >= 3) {
                        handleSearchS3Images();
                      }
                    }}
                    placeholder="Enter at least 3 characters to search (e.g., 'chicken', 'pizza')"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={handleSearchS3Images}
                    disabled={loadingImages || imageSearchQuery.trim().length < 3}
                    className="px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    {loadingImages ? 'Searching...' : 'Search'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Tip: Enter at least 3 characters to enable search. If image not found, use "AI Generate" or upload option.
                </p>
              </div>

              {/* Results */}
              {loadingImages ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto"></div>
                  <p className="text-gray-500 mt-4">Searching images...</p>
                </div>
              ) : !hasSearched ? (
                <div className="text-center py-12">
                  {/* Empty state - no icon, just clean space */}
                </div>
              ) : s3Images.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">No images found matching "{imageSearchQuery}"</p>
                  <p className="text-sm text-gray-500 mt-2">Try a different search term or:</p>
                  <div className="mt-4 flex gap-3 justify-center">
                    <button
                      onClick={() => {
                        setShowImageBrowser(false);
                        setAiImageTargetGallery(false); // Replace main image
                        setAIImageInput({
                          name: itemForm.name || '',
                          description: itemForm.description || ''
                        });
                        setShowAIImageModal(true);
                      }}
                      className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
                      style={{ backgroundColor: 'rgb(147, 51, 234)' }}
                    >
                      🎨 AI Generate Image
                    </button>
                    <label
                      htmlFor="image-upload-browser"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      📤 Upload Image
                    </label>
                    <input
                      type="file"
                      id="image-upload-browser"
                      accept="image/*"
                      onChange={(e) => {
                        setImageFile(e.target.files[0]);
                        setShowImageBrowser(false);
                      }}
                      className="hidden"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {s3Images.map((image, index) => (
                    <div
                      key={index}
                      onClick={() => selectS3Image(image.url, image.key)}
                      className="relative border-2 border-gray-200 rounded-lg overflow-hidden hover:border-theme-blue cursor-pointer transition-all group"
                    >
                      <img
                        src={image.url}
                        alt={image.filename}
                        className="w-full h-40 object-contain bg-gray-100"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                        <CheckCircle className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-all" />
                      </div>
                      <div className="p-2 bg-white">
                        <p className="text-xs text-gray-600 truncate" title={image.filename}>
                          {image.filename}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowImageBrowser(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* AI Generate Description Modal */}
      {showAIGenerateModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'rgb(147, 51, 234)' }}>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <h3 className="text-lg font-semibold text-white">AI Generate Description</h3>
              </div>
              <button
                onClick={() => {
                  setShowAIGenerateModal(false);
                  setAIGenerateInput({ name: '', description: '' });
                  setAIGenerateError('');
                }}
                className="text-white hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Name *
                </label>
                <input
                  type="text"
                  value={aiGenerateInput.name}
                  onChange={(e) => setAIGenerateInput({ ...aiGenerateInput, name: e.target.value })}
                  placeholder="Enter product name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rough Description *
                </label>
                <textarea
                  value={aiGenerateInput.description}
                  onChange={(e) => setAIGenerateInput({ ...aiGenerateInput, description: e.target.value })}
                  placeholder="Brief description of the product (e.g., 'premium dog food for large breeds')"
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Error Message */}
              {aiGenerateError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                  <svg className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-red-800">{aiGenerateError}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowAIGenerateModal(false);
                    setAIGenerateInput({ name: '', description: '' });
                    setAIGenerateError('');
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAIGenerateDescription}
                  disabled={generatingDescription || !aiGenerateInput.name || !aiGenerateInput.description}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  style={{ backgroundColor: 'rgb(147, 51, 234)' }}
                >
                  {generatingDescription ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Generate
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* AI Generate Image Modal */}
      {showAIImageModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col">
            {/* Sticky Header */}
            <div className="px-6 py-4 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: 'rgb(147, 51, 234)' }}>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <div>
                  <h3 className="text-lg font-semibold text-white">AI Generate Product Image</h3>
                  {aiImageTargetGallery && (
                    <span className="text-xs text-purple-200">Adding to Media Gallery</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAIImageModal(false);
                  setAIImageInput({ name: '', description: '' });
                  setAIImageError('');
                  setAiImageTargetGallery(false);
                  clearReferenceImage();
                  setUseReferenceImage(false);
                  setShowReferenceImageBrowser(false);
                }}
                className="text-white hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start">
                <svg className="w-5 h-5 text-theme-blue mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-xs text-blue-800">
                  AI will generate a professional product image based on your product name and description. Optionally, use a reference image to guide the style.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Name *
                </label>
                <input
                  type="text"
                  value={aiImageInput.name}
                  onChange={(e) => setAIImageInput({ ...aiImageInput, name: e.target.value })}
                  placeholder="Enter product name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Description
                </label>
                <textarea
                  value={aiImageInput.description}
                  onChange={(e) => setAIImageInput({ ...aiImageInput, description: e.target.value })}
                  placeholder="Optional: Add description to enhance the image generation (e.g., 'spicy chicken with vegetables')"
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Reference Image Section - Three Options */}
              <div className="border border-gray-200 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Reference Image Mode
                </label>
                
                {/* Radio Options */}
                <div className="space-y-2 mb-4">
                  {/* Option 1: No Reference */}
                  <label className="flex items-start p-2 rounded-lg cursor-pointer hover:bg-gray-50 border border-transparent has-[:checked]:border-purple-300 has-[:checked]:bg-purple-50">
                    <input
                      type="radio"
                      name="referenceMode"
                      value="none"
                      checked={referenceImageMode === 'none'}
                      onChange={() => {
                        setReferenceImageMode('none');
                        setUseReferenceImage(false);
                        clearReferenceImage();
                      }}
                      className="mt-0.5 w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                    />
                    <div className="ml-2">
                      <span className="text-sm font-medium text-gray-700">No Reference Image</span>
                      <p className="text-xs text-gray-500">Generate image using only product name and description</p>
                    </div>
                  </label>
                  
                  {/* Option 2: Use Reference Image */}
                  <label className="flex items-start p-2 rounded-lg cursor-pointer hover:bg-gray-50 border border-transparent has-[:checked]:border-purple-300 has-[:checked]:bg-purple-50">
                    <input
                      type="radio"
                      name="referenceMode"
                      value="reference"
                      checked={referenceImageMode === 'reference'}
                      onChange={() => {
                        setReferenceImageMode('reference');
                        setUseReferenceImage(true);
                      }}
                      className="mt-0.5 w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                    />
                    <div className="ml-2">
                      <span className="text-sm font-medium text-gray-700">Use as Style Reference</span>
                      <p className="text-xs text-gray-500">Generate a professional image based on the reference image style (e.g., upload a dish photo to create a better version)</p>
                    </div>
                  </label>
                  
                  {/* Option 3: Product Cover */}
                  <label className="flex items-start p-2 rounded-lg cursor-pointer hover:bg-gray-50 border border-transparent has-[:checked]:border-purple-300 has-[:checked]:bg-purple-50">
                    <input
                      type="radio"
                      name="referenceMode"
                      value="product_cover"
                      checked={referenceImageMode === 'product_cover'}
                      onChange={() => {
                        setReferenceImageMode('product_cover');
                        setUseReferenceImage(true);
                      }}
                      className="mt-0.5 w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                    />
                    <div className="ml-2">
                      <span className="text-sm font-medium text-gray-700">Use as Product Cover</span>
                      <p className="text-xs text-gray-500">Upload product packaging (bottle, packet, box) to generate image with product sample displayed alongside</p>
                    </div>
                  </label>
                </div>
                
                {/* Image Upload Section - Show when reference or product_cover is selected */}
                {(referenceImageMode === 'reference' || referenceImageMode === 'product_cover') && (
                  <div className="space-y-3 pt-3 border-t border-gray-200">
                    {/* Reference Image Preview */}
                    {referenceImageUrl && (
                      <div className="flex items-start gap-3">
                        <div className="relative group">
                          <img 
                            src={referenceImageUrl} 
                            alt="Reference" 
                            className="w-24 h-24 object-contain bg-gray-100 rounded-lg border border-gray-300"
                          />
                          <button
                            onClick={() => openImagePreview(referenceImageUrl, 0)}
                            className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                            type="button"
                          >
                            <Eye className="h-6 w-6 text-white" />
                          </button>
                          <button
                            onClick={clearReferenceImage}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            type="button"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="flex-1">
                          <span className="text-xs font-medium text-green-600 flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {referenceImageMode === 'product_cover' ? 'Product cover uploaded' : 'Reference image uploaded'}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            {referenceImageMode === 'product_cover' 
                              ? 'AI will generate an image showing this product packaging with the actual product sample'
                              : 'AI will use this as a style guide to create a professional version'
                            }
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Upload or Select Options */}
                    {!referenceImageUrl && (
                      <div>
                        <p className="text-xs text-gray-600 mb-2">
                          {referenceImageMode === 'product_cover' 
                            ? 'Upload an image of the product packaging (bottle, packet, box, etc.)'
                            : 'Upload an image to use as style reference'
                          }
                        </p>
                        <div className="flex gap-2">
                          <label className="flex-1 cursor-pointer">
                            <div className="px-3 py-2 border border-dashed border-gray-300 rounded-lg text-center hover:border-purple-500 hover:bg-purple-50 transition-colors">
                              <svg className="w-6 h-6 mx-auto text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                              </svg>
                              <span className="text-xs text-gray-600">Upload</span>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleReferenceImageUpload}
                              className="hidden"
                            />
                          </label>
                          
                          <button
                            onClick={() => {
                              setShowReferenceImageBrowser(true);
                              setReferenceSearchQuery('');
                              setReferenceS3Images([]);
                            }}
                            className="flex-1 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-center hover:border-purple-500 hover:bg-purple-50 transition-colors"
                            type="button"
                          >
                            <svg className="w-6 h-6 mx-auto text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-xs text-gray-600">Browse S3</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Image Model Selection */}
              {availableImageModels.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    AI Image Model
                  </label>
                  <select
                    value={selectedImageModel}
                    onChange={(e) => setSelectedImageModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm bg-white"
                  >
                    {availableImageModels.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose which AI model to use for generating the product image
                  </p>
                </div>
              )}

              {/* Error Message */}
              {aiImageError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                  <svg className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-red-800">{aiImageError}</p>
                </div>
              )}
            </div>

            {/* Sticky Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3 flex-shrink-0 bg-white rounded-b-xl">
              <button
                onClick={() => {
                  setShowAIImageModal(false);
                  setAIImageInput({ name: '', description: '' });
                  setAIImageError('');
                  clearReferenceImage();
                  setUseReferenceImage(false);
                  setShowReferenceImageBrowser(false);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAIGenerateImage}
                disabled={generatingImage || !aiImageInput.name}
                className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                style={{ backgroundColor: 'rgb(147, 51, 234)' }}
              >
                {generatingImage ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Generate Image
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* AI Generate Image Slider Modal */}
      {showAISliderModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Sticky Header */}
            <div className="px-6 py-4 flex items-center justify-between flex-shrink-0 rounded-t-xl" style={{ backgroundColor: 'rgb(236, 72, 153)' }}>
              <h3 className="text-lg font-semibold text-white">👗 AI Fashion Image Generator</h3>
              <button
                onClick={() => {
                  // Only close modal, keep face/clothing/prompt for reuse
                  setShowAISliderModal(false);
                  setSliderError('');
                  setGeneratedSliderImages([]);
                }}
                className="text-white hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="p-6 space-y-5 overflow-y-auto flex-1">
              {/* Info Banner */}
              <div className="bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-pink-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-pink-800">Generate Fashion / Clothing Images</h4>
                    <p className="text-xs text-pink-700 mt-1">
                      Upload a model/face reference and clothing images (sarees, dresses, suits). AI will generate multiple images showing the person wearing the outfit from different angles!
                    </p>
                  </div>
                </div>
              </div>

              {/* Two Column Layout for Face and Product */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Model/Face Reference Section */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-theme-blue rounded-full flex items-center justify-center mr-2">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-blue-800">
                          Model / Person
                        </label>
                        <span className="text-xs text-theme-blue">(Optional - 1 image)</span>
                      </div>
                    </div>
                    {faceReferenceImage && (
                      <button
                        onClick={removeFaceReferenceImage}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                        type="button"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  <p className="text-xs text-blue-700 mb-3">
                    Upload a model/person photo. AI will use their face, skin tone, and body type to show them wearing the clothing.
                  </p>
                  
                  {/* Face Image Preview */}
                  {faceReferenceImage ? (
                    <div className="relative mb-3 group">
                      <img
                        src={faceReferenceImage.url}
                        alt="Model Reference"
                        className="w-full h-32 object-contain bg-gray-100 rounded-lg border-2 border-blue-400"
                      />
                      <button
                        onClick={() => openImagePreview(faceReferenceImage.url, 0)}
                        className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                        type="button"
                      >
                        <Eye className="h-8 w-8 text-white" />
                      </button>
                      <div className="absolute bottom-2 left-2">
                        <span className="text-xs px-2 py-1 bg-theme-blue text-white rounded-full">
                          Model Reference
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <label className="flex-1 border-2 border-dashed border-blue-300 rounded-lg p-3 text-center hover:border-theme-blue transition-colors cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFaceReferenceImageUpload}
                          className="hidden"
                        />
                        <svg className="w-6 h-6 mx-auto text-blue-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span className="text-xs text-theme-blue">Upload Model</span>
                      </label>
                      <button
                        onClick={() => {
                          setShowFaceReferenceBrowser(true);
                          setFaceReferenceSearchQuery('');
                          setFaceReferenceS3Images([]);
                        }}
                        className="flex-1 border-2 border-dashed border-blue-300 rounded-lg p-3 text-center hover:border-theme-blue transition-colors"
                        type="button"
                      >
                        <svg className="w-6 h-6 mx-auto text-blue-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs text-theme-blue">Browse S3</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Clothing/Outfit Reference Section */}
                <div className="bg-pink-50 rounded-xl p-4 border border-pink-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center mr-2">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-pink-800">
                          Clothing / Outfit <span className="text-red-500">*</span>
                        </label>
                        <span className="text-xs text-pink-600">({sliderReferenceImages.length}/5 images)</span>
                      </div>
                    </div>
                    {sliderReferenceImages.length > 0 && (
                      <button
                        onClick={clearAllSliderReferenceImages}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                        type="button"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  
                  <p className="text-xs text-pink-700 mb-3">
                    Upload clothing images (saree, dress, suit from different angles). AI will use the fabric, color, pattern, and design.
                  </p>
                  
                  {/* Clothing Images Grid */}
                  {sliderReferenceImages.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {sliderReferenceImages.map((img, index) => (
                        <div key={img.id} className="relative group">
                          <img
                            src={img.url}
                            alt={`Clothing ${index + 1}`}
                            className="w-full h-16 object-contain bg-gray-100 rounded-lg border-2 border-pink-400"
                          />
                          <button
                            onClick={() => openImagePreview(img.url, 0)}
                            className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                            type="button"
                          >
                            <Eye className="h-5 w-5 text-white" />
                          </button>
                          <button
                            onClick={() => removeSliderReferenceImage(img.id)}
                            className="absolute -top-1 -right-1 p-0.5 bg-red-500 rounded-full text-white hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            type="button"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <span className="absolute bottom-0.5 left-0.5 text-[9px] px-1 py-0.5 bg-pink-600 text-white rounded">
                            {index + 1}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add Clothing Images Buttons */}
                  {sliderReferenceImages.length < 5 && (
                    <div className="flex gap-2">
                      <label className="flex-1 border-2 border-dashed border-pink-300 rounded-lg p-3 text-center hover:border-pink-500 transition-colors cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleSliderReferenceImageUpload}
                          className="hidden"
                          disabled={sliderReferenceImages.length >= 5}
                        />
                        <svg className="w-6 h-6 mx-auto text-pink-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span className="text-xs text-pink-600">Upload Clothing</span>
                      </label>
                      <button
                        onClick={() => {
                          setShowSliderReferenceBrowser(true);
                          setSliderReferenceSearchQuery('');
                          setSliderReferenceS3Images([]);
                        }}
                        disabled={sliderReferenceImages.length >= 5}
                        className="flex-1 border-2 border-dashed border-pink-300 rounded-lg p-3 text-center hover:border-pink-500 transition-colors"
                        type="button"
                      >
                        <svg className="w-6 h-6 mx-auto text-pink-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs text-pink-600">Browse S3</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Prompt Input with Default Template Button */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Generation Prompt <span className="text-red-500">*</span>
                  </label>
                  <button
                    onClick={() => setSliderPrompt(getDefaultFaceProductPrompt())}
                    className="text-xs px-3 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-full font-medium transition-colors"
                    type="button"
                  >
                    Use Default Template
                  </button>
                </div>
                <textarea
                  value={sliderPrompt}
                  onChange={(e) => setSliderPrompt(e.target.value)}
                  placeholder="Example: Generate a photorealistic image of this model wearing this saree. Show elegant draping with the exact fabric pattern and colors. Professional fashion photography."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                  rows={4}
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500">Quick add:</span>
                  {['elegant draping', 'front view', 'side profile', 'back view', 'traditional style', 'wedding look', 'party style'].map(tag => (
                    <button
                      key={tag}
                      onClick={() => setSliderPrompt(prev => prev + (prev ? ', ' : '') + tag)}
                      className="text-xs px-2 py-1 bg-gray-100 hover:bg-pink-100 rounded-full text-gray-600 hover:text-pink-700"
                      type="button"
                    >
                      + {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Image Count Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Images to Generate
                </label>
                <select
                  value={sliderImageCount}
                  onChange={(e) => setSliderImageCount(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm bg-white"
                >
                  {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <option key={num} value={num}>{num} images</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Each image will have a different angle/style variation automatically applied
                </p>
              </div>

              {/* AI Model Selector */}
              {availableImageModels.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    AI Image Model
                  </label>
                  <select
                    value={selectedImageModel}
                    onChange={(e) => setSelectedImageModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm bg-white"
                  >
                    {availableImageModels.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Error Message */}
              {sliderError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                  <svg className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-red-800">{sliderError}</p>
                </div>
              )}

              {/* Generation Progress */}
              {generatingSliderImages && (
                <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-pink-800">Generating images...</span>
                    <span className="text-sm text-pink-600">{sliderGenerationProgress}%</span>
                  </div>
                  <div className="w-full bg-pink-200 rounded-full h-2">
                    <div 
                      className="bg-pink-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${sliderGenerationProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-pink-600 mt-2">
                    Generated {generatedSliderImages.length} of {sliderImageCount} images
                  </p>
                </div>
              )}

              {/* Generated Images Preview */}
              {generatedSliderImages.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Generated Images ({generatedSliderImages.length})
                  </label>
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {generatedSliderImages.map((img) => (
                      <div key={img.id} className="relative group">
                        <img
                          src={img.url}
                          alt="Generated"
                          className="w-full h-24 object-contain bg-gray-100 rounded-lg border border-gray-200"
                        />
                        <button
                          onClick={() => removeGeneratedSliderImage(img.id)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          type="button"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sticky Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3 flex-shrink-0 bg-white rounded-b-xl">
              <button
                onClick={() => {
                  // Just close, keep data for reuse
                  setShowAISliderModal(false);
                  setSliderError('');
                  setGeneratedSliderImages([]);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  // Reset all data
                  setSliderPrompt('');
                  setSliderError('');
                  setGeneratedSliderImages([]);
                  clearAllSliderReferenceImages();
                  setFaceReferenceImage(null);
                }}
                className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50"
              >
                Reset All
              </button>
              
              {generatedSliderImages.length > 0 ? (
                <button
                  onClick={addGeneratedImagesToGallery}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors flex items-center justify-center"
                  style={{ backgroundColor: 'rgb(34, 197, 94)' }}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Add {generatedSliderImages.length} to Gallery
                </button>
              ) : (
                <button
                  onClick={handleGenerateSliderImages}
                  disabled={generatingSliderImages || !sliderPrompt.trim() || sliderReferenceImages.length === 0}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  style={{ backgroundColor: 'rgb(236, 72, 153)' }}
                >
                  {generatingSliderImages ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Generate {sliderImageCount} Images
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Slider Reference Image Browser Modal */}
      {showSliderReferenceBrowser && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'rgb(236, 72, 153)' }}>
              <h3 className="text-lg font-semibold text-white">Select Reference Image ({sliderReferenceImages.length}/5)</h3>
              <button
                onClick={() => setShowSliderReferenceBrowser(false)}
                className="text-white hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="mb-6">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={sliderReferenceSearchQuery}
                    onChange={(e) => setSliderReferenceSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && sliderReferenceSearchQuery.trim().length >= 3) {
                        handleSliderReferenceSearch();
                      }
                    }}
                    placeholder="Search images (min 3 characters)..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={handleSliderReferenceSearch}
                    disabled={loadingSliderReferenceImages || sliderReferenceSearchQuery.trim().length < 3}
                    className="px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: 'rgb(236, 72, 153)' }}
                  >
                    {loadingSliderReferenceImages ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>

              {loadingSliderReferenceImages ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600 mx-auto"></div>
                  <p className="text-gray-500 mt-4">Searching images...</p>
                </div>
              ) : sliderReferenceS3Images.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Enter at least 3 characters to search</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {sliderReferenceS3Images.map((image, index) => {
                    const isAlreadySelected = sliderReferenceImages.some(ref => ref.url === image.url);
                    return (
                      <div
                        key={index}
                        onClick={() => {
                          if (!isAlreadySelected && sliderReferenceImages.length < 5) {
                            addSliderReferenceFromS3(image.url);
                          }
                        }}
                        className={`relative border-2 rounded-lg overflow-hidden transition-all group ${
                          isAlreadySelected 
                            ? 'border-green-500 opacity-75 cursor-not-allowed' 
                            : sliderReferenceImages.length >= 5 
                              ? 'border-gray-200 opacity-50 cursor-not-allowed'
                              : 'border-gray-200 hover:border-pink-500 cursor-pointer'
                        }`}
                      >
                        <img
                          src={image.url}
                          alt={image.filename}
                          className="w-full h-32 object-contain bg-gray-100"
                        />
                        {isAlreadySelected ? (
                          <div className="absolute inset-0 bg-green-500 bg-opacity-30 flex items-center justify-center">
                            <CheckCircle className="h-8 w-8 text-green-600" />
                          </div>
                        ) : (
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                            <Plus className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-all" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowSliderReferenceBrowser(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Face Reference Browser Modal */}
      {showFaceReferenceBrowser && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 flex items-center justify-between bg-theme-blue">
              <h3 className="text-lg font-semibold text-white">Select Face Reference Image</h3>
              <button
                onClick={() => setShowFaceReferenceBrowser(false)}
                className="text-white hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="mb-6">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={faceReferenceSearchQuery}
                    onChange={(e) => setFaceReferenceSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && faceReferenceSearchQuery.trim().length >= 3) {
                        searchFaceReferenceImages();
                      }
                    }}
                    placeholder="Search face images (min 3 characters)..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={searchFaceReferenceImages}
                    disabled={loadingFaceReferenceImages || faceReferenceSearchQuery.trim().length < 3}
                    className="px-6 py-2 text-sm font-medium text-white bg-theme-blue rounded-lg hover:bg-theme-blue disabled:opacity-50"
                  >
                    {loadingFaceReferenceImages ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>

              {loadingFaceReferenceImages ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto"></div>
                  <p className="text-gray-500 mt-4">Searching images...</p>
                </div>
              ) : faceReferenceS3Images.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Enter at least 3 characters to search for face images</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {faceReferenceS3Images.map((image, index) => (
                    <div
                      key={index}
                      onClick={() => addFaceReferenceFromS3(image.url)}
                      className="relative border-2 rounded-lg overflow-hidden transition-all group border-gray-200 hover:border-theme-blue cursor-pointer"
                    >
                      <img
                        src={image.url}
                        alt={image.filename}
                        className="w-full h-32 object-contain bg-gray-100"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                        <Plus className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-all" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowFaceReferenceBrowser(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Reference Image Browser Modal - Same style as Browse Images */}
      {showReferenceImageBrowser && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'rgb(147, 51, 234)' }}>
              <h3 className="text-lg font-semibold text-white">Browse Images</h3>
              <button
                onClick={() => setShowReferenceImageBrowser(false)}
                className="text-white hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Search Box */}
              <div className="mb-6">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={referenceSearchQuery}
                    onChange={(e) => setReferenceSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && referenceSearchQuery.trim().length >= 3) {
                        handleReferenceSearchS3Images();
                      }
                    }}
                    placeholder="Enter at least 3 characters to search (e.g., 'chicken', 'pizza')"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={handleReferenceSearchS3Images}
                    disabled={loadingReferenceImages || referenceSearchQuery.trim().length < 3}
                    className="px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: 'rgb(147, 51, 234)' }}
                  >
                    {loadingReferenceImages ? 'Searching...' : 'Search'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Tip: Enter at least 3 characters to search. Select an image to use as reference for AI generation.
                </p>
              </div>

              {/* Results */}
              {loadingReferenceImages ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="text-gray-500 mt-4">Searching images...</p>
                </div>
              ) : referenceS3Images.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  {referenceSearchQuery.trim().length >= 3 ? (
                    <>
                      <p className="text-gray-600 font-medium">No images found matching "{referenceSearchQuery}"</p>
                      <p className="text-sm text-gray-500 mt-2">Try a different search term</p>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-600 font-medium">Search for images</p>
                      <p className="text-sm text-gray-500 mt-2">Enter at least 3 characters to search</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {referenceS3Images.map((image, index) => (
                    <div
                      key={index}
                      onClick={() => {
                        setReferenceImageUrl(image.url);
                        setReferenceImageFile(null);
                        setShowReferenceImageBrowser(false);
                      }}
                      className="relative border-2 border-gray-200 rounded-lg overflow-hidden hover:border-purple-500 cursor-pointer transition-all group"
                    >
                      <img
                        src={image.url}
                        alt={image.filename}
                        className="w-full h-40 object-contain bg-gray-100"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                        <CheckCircle className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-all" />
                      </div>
                      <div className="p-2 bg-white">
                        <p className="text-xs text-gray-600 truncate" title={image.filename}>
                          {image.filename}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowReferenceImageBrowser(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Media Gallery Browser Modal */}
      {showMediaGalleryBrowser && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <h3 className="text-lg font-semibold text-white">Add Image to Gallery</h3>
              <button
                onClick={() => setShowMediaGalleryBrowser(false)}
                className="text-white hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Search Box */}
              <div className="mb-6">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={mediaGallerySearchQuery}
                    onChange={(e) => setMediaGallerySearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && mediaGallerySearchQuery.trim().length >= 3) {
                        handleMediaGallerySearch();
                      }
                    }}
                    placeholder="Enter at least 3 characters to search (e.g., 'chicken', 'pizza')"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={handleMediaGallerySearch}
                    disabled={loadingMediaGalleryImages || mediaGallerySearchQuery.trim().length < 3}
                    className="px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    {loadingMediaGalleryImages ? 'Searching...' : 'Search'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Tip: Enter at least 3 characters to search. Click an image to add it to the gallery.
                </p>
              </div>

              {/* Results */}
              {loadingMediaGalleryImages ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto"></div>
                  <p className="text-gray-500 mt-4">Searching images...</p>
                </div>
              ) : mediaGalleryS3Images.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  {mediaGallerySearchQuery.trim().length >= 3 ? (
                    <>
                      <p className="text-gray-600 font-medium">No images found matching "{mediaGallerySearchQuery}"</p>
                      <p className="text-sm text-gray-500 mt-2">Try a different search term</p>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-600 font-medium">Search for images</p>
                      <p className="text-sm text-gray-500 mt-2">Enter at least 3 characters to search</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {mediaGalleryS3Images.map((image, index) => (
                    <div
                      key={index}
                      onClick={() => {
                        addMediaToGallery(image.url, image.key, 'image');
                        setShowMediaGalleryBrowser(false);
                      }}
                      className="relative border-2 border-gray-200 rounded-lg overflow-hidden hover:border-theme-blue cursor-pointer transition-all group"
                    >
                      <img
                        src={image.url}
                        alt={image.filename}
                        className="w-full h-40 object-contain bg-gray-100"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                        <Plus className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-all" />
                      </div>
                      <div className="p-2 bg-white">
                        <p className="text-xs text-gray-600 truncate" title={image.filename}>
                          {image.filename}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowMediaGalleryBrowser(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Manage Product Categories Modal */}
      {showProductCategoriesModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <h3 className="text-lg font-semibold text-white">
                Manage Product Categories - {activeMenuCategory?.name}
              </h3>
              <button
                onClick={() => setShowProductCategoriesModal(false)}
                className="text-white hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              {/* Add New Category */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add New Category
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddProductCategory();
                      }
                    }}
                    placeholder="Enter category name"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={handleAddProductCategory}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
                    style={{ backgroundColor: 'rgb(34, 197, 94)' }}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Categories List */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Existing Categories ({managedCategories.length})
                </label>
                {managedCategories.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    No categories yet. Add your first category above.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {managedCategories.map((cat) => {
                      const productsCount = menuItems.filter(item => item.category === cat).length;
                      
                      return (
                        <div
                          key={cat}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-900">{cat}</p>
                            <p className="text-xs text-gray-500">
                              {productsCount} product{productsCount !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <button
                            onClick={() => setDeletingCategory(cat)}
                            className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                            title={productsCount > 0 ? `Delete category (${productsCount} product(s) will be updated)` : 'Delete category'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowProductCategoriesModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Category Confirmation Modal */}
      {deletingCategory && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Delete Product Category?
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Are you sure you want to delete the category "{deletingCategory}"? 
                {menuItems.filter(item => item.category === deletingCategory).length > 0 && (
                  <span className="block mt-2 text-orange-600 font-medium">
                    {menuItems.filter(item => item.category === deletingCategory).length} product(s) using this category will have their category removed.
                  </span>
                )}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeletingCategory(null)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteProductCategory(deletingCategory)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Single Item Confirmation Modal */}
      {showDeleteProductModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Delete Product?
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Are you sure you want to delete this product? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteProductModal(false);
                    setItemToDelete(null);
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteItem}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete All Items Confirmation Modal */}
      {deleteAllConfirm && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Delete All Products?
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Are you sure you want to delete <span className="font-bold text-red-600">ALL {menuItems.length} products</span> in this category? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteAllConfirm(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteAll}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  Delete All
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Image/Video Preview Modal with Slider */}
      {showImagePreview && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-50"
          onClick={() => setShowImagePreview(false)}
        >
          {/* Media Container - positioned relative for controls inside */}
          <div 
            className="relative inline-block"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Render Image or Video based on current media type */}
            {(() => {
              const currentMedia = previewImages[previewImageIndex];
              const isVideo = currentMedia?.type === 'video';
              const url = previewImageUrl;
              
              if (isVideo && isYouTubeUrl(url)) {
                // YouTube Video - Embedded Player
                const videoId = getYouTubeVideoId(url);
                return (
                  <div className="w-[90vw] max-w-4xl aspect-video bg-black rounded-lg overflow-hidden">
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                      title="YouTube video"
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                );
              } else if (isVideo || isVideoFile(url)) {
                // Local/S3 Video - HTML5 Video Player
                return (
                  <div className="w-[90vw] max-w-4xl aspect-video bg-black rounded-lg overflow-hidden">
                    <video
                      src={url}
                      className="w-full h-full"
                      controls
                      autoPlay
                      playsInline
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                );
              } else {
                // Image
                return (
                  <img
                    src={url}
                    alt="Preview"
                    className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
                  />
                );
              }
            })()}
            
            {/* Close Button - Top Right */}
            <button
              onClick={() => setShowImagePreview(false)}
              className="absolute top-3 right-3 p-2 bg-gray-800 bg-opacity-60 rounded-full hover:bg-opacity-80 transition-all z-10"
              type="button"
            >
              <X className="h-5 w-5 text-white" />
            </button>
            
            {/* Media Type Badge - Top Left */}
            {previewImages[previewImageIndex]?.type === 'video' && (
              <div className="absolute top-3 left-3 px-2 py-1 bg-purple-600 bg-opacity-80 rounded-full z-10">
                <span className="text-white text-xs font-medium">Video</span>
              </div>
            )}
            
            {/* Bottom Controls Bar */}
            {previewImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-gray-800 bg-opacity-60 rounded-full px-2 py-1.5 z-10">
                {/* Previous Button */}
                <button
                  onClick={prevPreviewImage}
                  className="p-1.5 hover:bg-white hover:bg-opacity-20 rounded-full transition-all"
                  type="button"
                >
                  <ChevronLeft className="h-5 w-5 text-white" />
                </button>
                
                {/* Counter */}
                <span className="text-white text-sm font-medium min-w-[50px] text-center">
                  {previewImageIndex + 1} / {previewImages.length}
                </span>
                
                {/* Next Button */}
                <button
                  onClick={nextPreviewImage}
                  className="p-1.5 hover:bg-white hover:bg-opacity-20 rounded-full transition-all"
                  type="button"
                >
                  <ChevronRight className="h-5 w-5 text-white" />
                </button>
              </div>
            )}
            
            {/* Dots - Below the control bar */}
            {previewImages.length > 1 && (
              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                {previewImages.map((media, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewImageIndex(idx);
                      setPreviewImageUrl(previewImages[idx].url);
                    }}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === previewImageIndex 
                        ? (media.type === 'video' ? 'bg-purple-400 w-4' : 'bg-white w-4')
                        : (media.type === 'video' ? 'bg-purple-400 bg-opacity-50 hover:bg-opacity-75' : 'bg-white bg-opacity-50 hover:bg-opacity-75')
                    }`}
                    type="button"
                    title={media.type === 'video' ? 'Video' : 'Image'}
                  />
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Category Filter Overlay */}
      {showCategoryOverlay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[60vh] overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Filter by Category</h3>
              <button
                onClick={() => setShowCategoryOverlay(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            <div className="p-2 overflow-y-auto max-h-[calc(60vh-48px)]">
              <button
                onClick={() => {
                  setSelectedCategory('');
                  setShowCategoryOverlay(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-md mb-1 transition-colors flex items-center justify-between text-sm ${
                  selectedCategory === ''
                    ? 'bg-[rgb(41,173,211)] text-white'
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                }`}
              >
                <span className="font-medium">All Categories</span>
                <span className={`text-xs ${
                  selectedCategory === '' ? 'text-white' : 'text-gray-500'
                }`}>
                  ({menuItems.length})
                </span>
              </button>
              {managedCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setShowCategoryOverlay(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md mb-1 transition-colors flex items-center justify-between text-sm ${
                    selectedCategory === cat
                      ? 'bg-[rgb(41,173,211)] text-white'
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span className="font-medium">{cat}</span>
                  <span className={`text-xs ${
                    selectedCategory === cat ? 'text-white' : 'text-gray-500'
                  }`}>
                    ({menuItems.filter(item => item.category === cat).length})
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuManagement;
